import {
    IClientMinimalData,
    ICompanyBrandingData,
    IUserBrandingData
} from '~/components/ClientExperience/GiftCard/types/client-exprience.types';
import {
    IContainerBlockStyles,
    IImageBlockStyles,
    IInvoiceHeaderBlockStyles,
    IServiceSelectionBlockStyles,
    ISocialBlockStyles,
    ISpacerBlockStyles,
    IStyles,
    IVideoBlockStyles
} from '~/types/block-styles.types';
import {
    CompanyInfoLayoutType,
    TBlock,
    TQuestion,
    TScheduleTriggerType
} from '~/types/builder.types';
import {
    CompositionType,
    IABTests,
    IAccountSettings,
    IAttribution,
    IAuditeeCompany,
    ICanceled,
    IClientPortalConfiguration,
    ICompanyFont,
    IContactForm,
    IContract,
    ICustomProjectField,
    IEmailAttachment,
    IEmailSignature,
    IEvent,
    IFeed,
    IFileAsset,
    IFileTemplate,
    IFlow,
    IFlowActionSummary,
    IFlowConfigurationMembers,
    IFlowConfigurationResponse,
    IFlowPublicConfiguration,
    IFlowSummary,
    IFlowTemplate,
    IFlowTheme,
    IFlowThumbnail,
    IFolder,
    IImage,
    IInvoiceHeader,
    IInvoiceSummary,
    IOwner,
    IPaymentContent,
    IPricingPlan,
    IQuickbooksIntegration,
    IRecurringPayment,
    IRecurringSchedule,
    IService,
    ISignature,
    ISubscription,
    ITeamMember,
    ITrial,
    IVendorEmail,
    PaymentStatus,
    ServiceItemType,
    ServiceItemUnit,
    ServiceSelectionType,
    ServicesItemsLayoutType,
    SignatureTypes,
    SystemUserType,
    TActionBlocks,
    TaxType,
    TBlockType,
    TContainerableBlockType,
    TFlowStatus,
    TFlowTag,
    TGratuityTypes,
    TInvoiceType,
    TLayoutType,
    IProjectDate,
    IInvoice,
    IAgreement,
    IProposal,
    IVendorPackage,
    IImageAsset,
    IFlowPermission,
    DiscountTypes
} from '~/types/model.types';
import { IContainerPageStyles } from '~/types/page-styles.types';
import { CreditCardProcessor, PaymentMethodType } from '~/types/pay.types';
import {
    TPlanCode,
    IInvoice as ISubscriptionInvoice
} from '~/components/Membership/types/membership.types';
import { Company } from '~/models/company.model';
import {
    IFlowGalleryTemplate,
    TemplateType,
    TRepoKey
} from '~/components/FlowGallery/types/flow-gallery.types';
import { PartialDeep, SetOptional, SetRequired, ValueOf } from 'type-fest';
import { TFullTimezone, TShortTimezone } from '~/utils/timezone.utils';
import { FlowConfigurationModel } from '~/models/flow-configuration.model';
import { IAccessRequestCounts } from '~/components/Settings/ProAccess/ProView/AccessRequestsStatusFilter';
import { TFeaturePlan } from '~/components/FeatureRestriction/types/feature-restriction.types';
import {
    ISessionDuration,
    ISessionReminder,
    TAvailabilityIncrements,
    TSessionColors,
    TSessionDurationUnit,
    TSessionNoticeUnit,
    TSessionRollingWindowDurationUnit
} from '~/apps/main/Scheduler/pages/ConfigureSession/utils/configure-session.types';
import { RolloutFeatureFlags } from '~/services/feature.service';
import { TIntentType } from '~/components/IntentHelpers/intent.utils';
import { ProAccountAccessAuthorization } from '~/models/pro-access-request.model';
import { CompanyTypeKeyEnum, TCompanyTypeKeyEnum } from '~/utils/company.utils';
import {
    AutoExpirationState,
    DurationUnitType,
    EmailTrackingSource,
    EmailType,
    EventType,
    FlowStartExpirationCondition,
    FlowTag,
    FlowType,
    HbUserType,
    MeetingType,
    PaymentType,
    PipelineAutomationType,
    PipelineStageCategory,
    PipelineStageGroup,
    PipelineStageType,
    PvlFlowType,
    SessionCallerType,
    SessionType,
    SessionWindowType,
    SourceType,
    TemplateSourceType,
    UserType,
    UserTypeDisplayName,
    VideoConferenceType,
    WorkflowAutomationTimeFrame,
    WorkflowAutomationType,
    WorkspaceFileStatus,
    WorkspaceFileType,
    WorkspaceMemberStatus,
    WorkspaceOnboardingBotState,
    WorkspaceRole,
    WorkspaceSourceType,
    WorkspaceTabType,
    WorkspaceType,
    TMethod2FA
} from '~/types/enums.types';
import {
    ClientPendingTask,
    ClientPendingTaskType,
    PendingTaskStatus
} from '~/common/hooks/ClientPendingTask/client-pending-task.types';
import {
    IExistingClientInOrganizationFields,
    INewClientInOrganizationFields,
    IOrganization
} from '~/components/OrganizationView/types/organization-view.types';
import type { IAppStates } from '~/apps/main/App/hooks/useNavigation.consts';
import { z } from 'zod';
import { IAutomationInstance } from '~/types/automation.types';
import { TDayOfWeek } from '~/types/app.types';
import { ICreateCalendarItemResponse } from '~/types/calendar-item.types';
import type {
    THBCalloutBannerIllustrationAspectRatio,
    THBCalloutBannerTheme,
    THBCalloutBannerType
} from '@honeybook/hbui/dist/components/HBCalloutBanner/HBCalloutBanner.types';
import { HBCalloutCardProps } from '@honeybook/hbui/dist/components/HBCalloutCard/HBCalloutCard.types';
import { THBCalloutBannerSize } from '@honeybook/hbui/dist/components/HBCalloutBanner/HBCalloutBanner.types';
import {
    ICustomFieldSchema,
    ICustomFieldValue
} from '~/components/CustomFields/custom-field-schemas.types';
import { TPipelineView } from '~/apps/main/Pipeline/pipeline.types';
import {
    TInquiryFollowUpTimeFrameValue,
    TCompanySpaceUpdate
} from '~/apps/main/CompanySettings/CompanyPreferences/server-types';
import { OnboardingStepNames } from '~/components/IntentOnboarding/types/onboarding.types';
import { TWorkspaceCreator } from '~/apps/main/Workspace/server-types';
import { IOneComposerRecipient } from '~/components/OneComposer/OneComposer.types';
import { FirebaseOptions } from 'firebase/app';

export enum TemplateDeleteError {
    CannotDeleteWorkflowRelatedTemplate = 'CannotDeleteWorkflowRelatedTemplate',
    HBEntityLinkedToWorkflowsError = 'HBEntityLinkedToWorkflowsError',
    CannotDeleteDraftRelatedTemplate = 'CannotDeleteDraftRelatedTemplate'
}

export const TWorkflowAutomationInfo = z.object({
    // _id: z.string(),
    // automation_type_cd: z.number(), // ordinal of enum in Ruby, auto-calculated from WorkflowAutomationTypeValues
    automation_type: WorkflowAutomationType,
    relevant_time_frame: WorkflowAutomationTimeFrame.or(z.undefined()),
    is_automation_on: z.boolean(),
    email_template_id: z.string().nullable(),
    questionnaire_template_id: z.string().nullable(),
    flow_template_id: z.string().nullable(),
    // brochure_container_template_id: z.string().nullable(),
    // event_type: z.null(),
    subject: z.string().nullable(),
    html_body: z.string().nullable()
});
export type IWorkflowAutomationInfo = z.infer<typeof TWorkflowAutomationInfo>;

export type TVariableType =
    | 'invoice_container_variable'
    | 'invoice_header_variable'
    | 'invoice_summary_variable'
    | 'payment_variable'
    | 'company_info_variable'
    | 'charge_variable'
    | 'contract_field_variable'
    | 'schedule_container_variable'
    | 'recurring_schedule_variable'
    | 'service_selection_variable'
    | 'service_selection_item_variable'
    | 'date_question_variable'
    | 'multi_select_question_variable'
    | 'selected_value_question_variable'
    | 'single_value_question_variable'
    | 'upload_attachments_question_variable';

export interface ITransformedPageResponse extends IPageResponse {
    completed: boolean;
    views: IPageInfoView[];
    submissions?: IPageSubmission[];
}

export interface IS3UploadFileResponse {
    data: string;
    file_name: string;
    internalId: string;
    headers?: Record<string, string>;
}

export interface IBaseBlockResponse {
    block_id: string;
    page_id: string;
    flow_id: string;
    flow_revision_id?: string;
    type: TBlockType;
    styles: IStyles;
    // This doesn't actually appear on the response from the server
    // We add it in a transformation in the flow service
    // But.... we didn't want to create a milion types for all block responses + parent_id
    // So, it's here.
    parent_id?: string;
    connected_variable_id?: string;
    critical_changes?: Array<string>;
    discarded?: boolean;
}

export interface IFeedResponse {
    company_id: string;
    creator: {
        company_id: string;
        email: string;
        full_name: string;
    };
    deletedMembers: Partial<IMember>[];
    dismissed_feed_pvl_request: string;
    feed: IFeed;
    workspace_pipeline_data: IWorkspacePipelineDataResponse;
    workspace_source: WorkspaceSourceType;
}

export interface IContainerableBlockResponse extends IBaseBlockResponse {
    type: TContainerableBlockType;
    layout_type: TLayoutType;
    children: IChildLayoutResponse[];
    styles: IContainerBlockStyles;
    background_image?: IImage;
    container_styles?: IContainerBlockStyles;
    source_flow_id?: string;
    source_block_id?: string;
}

export interface IContainerBlockBase extends IContainerableBlockResponse {
    type: 'container_block' | 'company_info_block' | 'lead_details_block';
    composition_type?: CompositionType;
    background_image?: IImage;
}

export interface ILeadDetailsBlockResponse extends IContainerBlockBase {
    type: 'lead_details_block';
}

export interface IContainerBlockResponse extends IContainerBlockBase {
    type: 'container_block';
}

export interface IFullstoryTokenResponse {
    is_fullstory_record_enabled?: boolean;
}

export interface ICompanyInfoBlockResponse extends IContainerBlockBase {
    type: 'company_info_block';
    composition_type?: CompositionType.CompanyInfo;
    company_info_layout_type: CompanyInfoLayoutType;
    linked_to_company_settings: boolean;
}

export interface ITextBlockResponse extends IBaseBlockResponse {
    type: 'text_block';
    content: string;
}

export interface ISocialBlockResponse extends IBaseBlockResponse {
    type: 'social_block';
    social_styles?: Partial<ISocialBlockStyles>;
}

export interface IQuestionBlockResponse extends IBaseBlockResponse {
    type: 'question_block';
    question?: TQuestion;
}

export interface IContractBlockResponse extends IContainerableBlockResponse {
    type: 'contract_block';
}

export interface IContractHTMLBlockResponse extends IBaseBlockResponse {
    type: 'contract_html_block';
    contract: IContract;
}

export interface ISignaturesBlockResponse extends IContainerableBlockResponse {
    type: 'signatures_block';
}

export interface ISignatureBlockResponse extends IBaseBlockResponse {
    type: 'signature_block';
    signature: ISignature;
}

export interface IVideoBlockResponse extends IBaseBlockResponse {
    type: 'video_block';
    styles: IVideoBlockStyles;
}

export interface ISpacerBlockResponse extends IBaseBlockResponse {
    type: 'spacer_block';
    spacer_styles?: Partial<ISpacerBlockStyles>;
}

export interface IInvoiceBlockResponse extends IContainerableBlockResponse {
    type: 'invoice_container_block';
    invoice_type: TInvoiceType;
    sub_total?: number;
    total_tax?: number;
    total_discount?: number;
    total_price?: number;
}

export interface IScheduleContainerBlockResponse
    extends IContainerableBlockResponse {
    type: 'schedule_container_block';
    session_config?: ISessionConfig;
    is_required: boolean;
    schedule_session_trigger: TScheduleTriggerType;
    session_config_id?: string;
    session_config_hash?: string;
}

export interface IScheduleSessionBlockResponse extends IBaseBlockResponse {
    type: 'schedule_session_block';
}

export interface IScheduleSessionConfigsResponse {
    _id: string;
    session_configs: ISessionConfig[];
}

export interface ICalendarItem {
    type: MeetingType;
    title: string;
    date_time_start: string;
    phone_call_number?: string;
    video_meeting_link?: string;
    client_timezone: TShortTimezone;
    location?: string;
    session_description?: string;
    google_calendar_link: string;
    client_id: string;
    confirmation_message?: string;
    date_time_end: string;
    owner_id: string;
    timezone: TShortTimezone;
    allow_cancel: boolean;
    allow_reschedule: boolean;
}

export type ScheduleContainerVariableStatus =
    | 'canceled'
    | 'scheduled'
    | 'default';

export interface ScheduleContainerVariableClient {
    _id: string;
    full_name: string;
    email: string;
}

export interface ICalendarItemBase {
    session_description: string;
    type: MeetingType;
    location?: string;
    phone_call_number?: string;
    video_meeting_link?: string;
    date_time_start: string;
    client_timezone: TShortTimezone;
    client_id: string;
    owner_id: string;
}

export interface IScheduleContainerVariableValue {
    session_config_id?: string;
    date_time_start?: string;
    date_time_end?: string;
    client_timezone?: TFullTimezone;
    member_timezone?: TFullTimezone;
    client?: ScheduleContainerVariableClient;
    owner_id?: string;
    calendar_item_id?: string;
    status?: ScheduleContainerVariableStatus;
    session_description?: string;
    session_type?: SessionType;
    meeting_location?: string;
    phone_call_number?: string;
    video_meeting_link?: string;
    session_duration?: ISessionDuration<TSessionDurationUnit>;
    session_name?: string;
    canceled_by?: string;
    cancel_message?: string;
    google_calendar_link?: string;
    allow_canceling?: boolean;
    allow_rescheduling?: boolean;
    was_session_config_changed?: boolean;
    session_config_hash?: string;
    calendar_item_id_pending_cancel?: string;
}

export interface IScheduleAvailabilitiesSingleSlotResponse {
    startTime: string;
    blockers?: ISlotBlockers[];
    userData?: ISlotUserData[];
}

export interface IScheduleAvailabilitiesSingleDateResponse {
    date: string;
    slots: IScheduleAvailabilitiesSingleSlotResponse[];
}

export interface IScheduleAvailabilitiesResponse {
    dates: IScheduleAvailabilitiesSingleDateResponse[];
}

export interface IScheduleAvailabilities {
    [month: string]: {
        [date: string]: TSessionTimeSlot[];
    };
}

export interface IScheduleSlotAvailableMembersResponse {
    memberIds: string[];
}

export interface IImageBlockResponse extends IBaseBlockResponse {
    type: 'image_block';
    image: IImage;
    image_styles?: Partial<IImageBlockStyles>;
}

export interface IServicesBlockResponse extends IContainerableBlockResponse {
    type: 'services_block';
}

export interface IServiceItemBlockResponse extends IContainerableBlockResponse {
    type: 'service_selection_item_block' | 'invoice_service_item_block';
    service_type: ServiceItemType;
    service_item: IService;
    service_image: IImage;
    service_asset?: string;
    service_asset_enabled?: boolean;
}

export interface IInvoiceServiceItemBlockResponse
    extends IServiceItemBlockResponse {
    type: 'invoice_service_item_block';
}

export interface IServiceSelectionItemBlockResponse
    extends IServiceItemBlockResponse {
    type: 'service_selection_item_block';
    invoice_service_item: IService;
    allow_custom_quantity?: boolean;
    session_config_id: string | null;
    session_config: ISessionConfig | null;
    session_config_hash: string | null;
    min_quantity?: number;
    max_quantity?: number;
}

export type TServiceSelectionBlockType = 'service' | 'session';

export interface IServiceSelectionBlockResponse
    extends IContainerableBlockResponse {
    type: 'service_selection_block';
    selection_required: boolean;
    send_info_to_invoice: boolean;
    selection_type: ServiceSelectionType;
    service_selection_type: TServiceSelectionBlockType;
    show_total: boolean;
    discount: boolean;
    services_layout: ServicesItemsLayoutType;
    banner_text: string;
    service_selection_styles?: Partial<IServiceSelectionBlockStyles>;
    invoice_block_id: string;
}

// @ts-ignore
export interface IServiceItemsContainerBlockResponse
    extends IContainerBlockResponse {
    type: 'service_items_container_block';
}

export type TPaymentMethod =
    | 'general'
    | 'bank_account'
    | 'credit_card'
    | 'manual';

export enum PaymentMethods {
    bankAccount = 'bank_account',
    creditCard = 'credit_card'
}

export interface IPaymentScheduleBlockResponse
    extends IContainerableBlockResponse {
    type: 'payment_schedule_block';
}

export interface IRecurringScheduleBlockResponse
    extends IContainerableBlockResponse {
    type: 'recurring_schedule_block';
    recurring_schedule?: IRecurringSchedule;
    sent_invoices_count: number;
    last_invoice_date: string;
    upcoming_invoices_count: number;
    paid_payments_count: number;
    total_paid_amount: number;
    upcoming_invoices?: IRecurringPayment[];
    current_payment_block_id?: string;
}

export interface IRecurringInvoiceResponse
    extends IFlowResponse,
        IRecurringPayment {}

export interface IPaymentBlockResponse extends IBaseBlockResponse {
    type: 'payment_block';
    payment_content: IPaymentContent;
}

export interface IInvoiceHeaderBlockResponse extends IBaseBlockResponse {
    type: 'invoice_header_block';
    invoice_header: IInvoiceHeader;
    styles: IInvoiceHeaderBlockStyles;
}

export interface IInvoiceSummaryBlockResponse extends IBaseBlockResponse {
    type: 'invoice_summary_block';
    invoice_summary: IInvoiceSummary;
}

export interface IInvoiceColumnHeaderBlockResponse extends IBaseBlockResponse {
    type: 'invoice_column_header_block';
    show_image?: boolean;
}

export interface IChargeContainerBlockResponse
    extends IContainerableBlockResponse {
    type: 'charge_container_block';
}

export interface IChargeBlockResponse extends IBaseBlockResponse {
    type: 'charge_block';
    payment_method_types: TPaymentMethod[];
    allow_autopay: boolean;
    allow_gratuity: boolean;
    gratuity_type: TGratuityTypes;
}

export type TBlockResponse =
    | IChargeBlockResponse
    | IChargeContainerBlockResponse
    | ICompanyInfoBlockResponse
    | IContainerBlockResponse
    | IContractBlockResponse
    | IContractHTMLBlockResponse
    | IIframeBlockResponse
    | IImageBlockResponse
    | IInvoiceBlockResponse
    | IInvoiceHeaderBlockResponse
    | IInvoiceServiceItemBlockResponse
    | IInvoiceSummaryBlockResponse
    | ILeadDetailsBlockResponse
    | IPaymentBlockResponse
    | IPaymentScheduleBlockResponse
    | IQuestionBlockResponse
    | IRecurringScheduleBlockResponse
    | IScheduleContainerBlockResponse
    | IScheduleSessionBlockResponse
    | IServiceSelectionBlockResponse
    | IServiceSelectionItemBlockResponse
    | IServicesBlockResponse
    | IServiceItemBlockResponse
    | ISignatureBlockResponse
    | ISignaturesBlockResponse
    | ISocialBlockResponse
    | ISpacerBlockResponse
    | ITextBlockResponse
    | IVideoBlockResponse
    | IInvoiceColumnHeaderBlockResponse;

export type TDebugBlock = TBlockResponse | Record<string, any>;

export interface IChildLayoutResponse {
    block_id: string;
    size: number;
    order: number;
}

export interface ITransformedFlowResponse {
    flow: IFlow;
    pages: Record<string, ITransformedPageResponse>;
    blocks: Record<string, TBlockResponse>;
    variables: Record<string, IVariableResponse>;
}

export interface IFlowLastActionNumberResponse {
    last_action_number: number;
}

export interface IPendingChange {
    block_id: string;
    accepted: boolean;
}

export interface IVariableResponse<V = any> {
    connected_block_id: string;
    value: V;
    variable_id: string;
    raw_value?: any;
    invalidated_on: string;
    diverged_on: string;
    data_source_type?: string;
    data_source_property?: string;
    type?: TVariableType;
}

export interface IInvoiceHeaderVariableValue {
    title?: string;
    po_number?: string;
    invoice_number?: string;
    bill_to_content?: string;
    bill_to_user?: string;
}

export interface IInvoiceProjectDetails {
    project_name?: string;
    project_type?: string;
    project_location?: string;
    project_date?: string;
}

export interface IBillFromContent {
    company_icon_url?: string;
    company_icon?: IImage;
    is_default_company_icon?: boolean; // dev notice: is set to true for demo users in the BE (checking 'assets_users_demo' in icon url)
    company_name?: string;
    company_email?: string;
    company_phone_number?: string;
    company_address?: {
        city?: string;
        state?: string;
        street_address?: string;
        zip_code?: string;
        country?: string;
    };
}

export interface IPageResponse {
    page_id: string;
    flow_id?: string;
    flow_revision_id?: string;
    order: number;
    root_block_id: string;
    title: string;
    cta_text: string;
    styles?: IContainerPageStyles;
    background_image?: IImage;
    redirect_website_url: string; // TODO: Change when website URL is active
}

export interface IPageInfoView {
    user_id: string;
    page_id?: string;
    performed_at?: string;
}

export interface IPageSubmission {
    page_id?: string;
    performed_at: string;
    user_id: string;
}

export interface IPageInfoResponse {
    page_id: string;
    is_submitted: boolean;
    submissions: IPageSubmission[];
    submitted_on: string;
    views: IPageInfoView[];
    was_viewed: boolean;
}

export interface IMember {
    _id?: string;
    company?: Partial<ICompanyResponse>;
    companies?: Partial<Company>[];
    company_id?: string;
    member_company_info?: Partial<ICompanyResponse>;
    client_organization?: Pick<
        IOrganization,
        'name' | 'address' | 'phone_number'
    >;
    email?: string;
    is_unsubscribed?: boolean;
    full_name?: string;
    first_name?: string;
    last_name?: string;
    initials?: string;
    address?: string;
    phone_number?: string;
    profile_image?: IImage;
    profile_image_url?: string;
    is_real_chargeable_user?: boolean;
    system_user_type?: SystemUserType;
    is_pro?: boolean;
    is_educator?: boolean;
    is_disabled?: boolean;
    contact_id?: string;
    is_custom_project_fields_enabled?: boolean;
    password?: string;
    created_at?: string;
}

export interface IMemberClientOrganization {
    name: string;
    address?: string;
    phone_number?: string;
}

export type TDaylightTime = 'am' | 'pm';

export interface TSessionTimeSlot
    extends IScheduleAvailabilitiesSingleSlotResponse {
    isDisabled?: boolean;
    fakeDaylightType?: 'am' | 'pm';
}

export enum BlockerType {
    past = 'PAST',
    notice = 'NOTICE',
    rangeStart = 'RANGE_START',
    range_end = 'RANGE_END',
    meeting = 'MEETING'
}

export interface ISlotBlockers {
    metadata?: {};
    type?: BlockerType;
}

export interface ISlotUserData {
    userId: string;
    overlapMeeting?: IOverlapMeeting[];
}

export interface IOverlapMeeting {
    range: IMeetingRange;
    source: string;
    metadata: IMeetingMetadata;
}

export interface IMeetingRange {
    start: string;
    end: string;
}

export interface IMeetingMetadata {
    title?: string;
    workspace_id?: string;
    timezone?: string;
    flow_id?: string;
    event_id?: string;
}

export interface IDayAvailabilityTimeRange {
    range_time_start: string;
    range_time_end: string;
}

export interface IDayAvailability {
    _id?: string;
    session_config_id?: string;
    time_ranges: Array<IDayAvailabilityTimeRange>;
    day_of_week: TDayOfWeek | null;
    override_date: string | null;
}

export interface ISessionConfig {
    _id: string | null;
    allow_canceling: boolean;
    allow_rescheduling: boolean;
    availability_increments?: TAvailabilityIncrements;
    buffer_after?: ISessionDuration<TSessionDurationUnit>;
    buffer_before?: ISessionDuration<TSessionDurationUnit>;
    confirmation_message?: string;
    day_availabilities?: IDayAvailability[];
    enabled: boolean;
    fixed_end_date?: string;
    fixed_start_date?: string;
    meeting_location?: string;
    notice_period?: ISessionDuration<TSessionNoticeUnit>;
    phone_call_caller?: SessionCallerType;
    phone_call_number?: string;
    placement_id: string;
    related_workflows?: {
        _id: string;
        title: string;
    }[];
    rolling_window_duration?: ISessionDuration<TSessionRollingWindowDurationUnit>;
    schedule_behaviour_type: string;
    send_confirmation_email: boolean;
    session_color: TSessionColors;
    session_description: string;
    session_duration: ISessionDuration<TSessionDurationUnit>;
    session_name: string;
    session_reminders?: ISessionReminder[];
    session_timezone: TShortTimezone;
    session_type: SessionType;
    session_url: string;
    set_as_project_date: boolean;
    shared_member_ids: string[];
    shared_members?: IEnrichedSharedMember[];
    updated_at: string | null;
    user_id?: string;
    video_conference_type?: VideoConferenceType;
    video_meeting_link?: string;
    window_type: SessionWindowType;
}

export interface ISharedMember {
    _id: string;
    first_name: string;
    last_name: string;
}

export interface IEnrichedSharedMember extends ISharedMember {
    full_name?: string;
    profile_image_url?: string;
}

export interface ILastContentEdit {
    last_edited_on?: string;
    user_id?: string;
    user_full_name?: string;
}

export interface IPublisher {
    full_name?: string;
    profile_image_url?: string;
    initials?: string;
}

export interface IPublicPendingTaskPollingResponse {
    token: string;
    status: number;
    error?: string;
}

export interface IEmbeddedLeadFormScriptResponse {
    embed: {
        iframe_code: string;
        in_body: string;
        in_head: string;
        is_placement_snippet: boolean;
    };
}

export interface IFlowResponse {
    _id: string;
    actions: IFlowActionSummary[];
    action_block_types: Array<TActionBlocks>;
    published_at: string;
    last_changed_at: string;
    blocks: TBlockResponse[];
    canceled?: ICanceled;
    changes_pending_approval: IPendingChange[];
    company_id: string;
    company?: ICompanyResponse;
    publisher?: IPublisher;
    configuration: IFlowConfigurationResponse;
    created_at: string;
    currency: Currency;
    event_name?: string;
    expired: boolean;
    first_published_at: string;
    flow_type?: TFlowType;
    folder_id?: string;
    is_published: boolean;
    is_template: boolean;
    is_workspace_archived?: boolean;
    last_action_number: number;
    members?: IMember[];
    pages_info: IPageInfoResponse[];
    pages: IPageResponse[];
    path?: string;
    project_id?: string;
    revision_id?: string;
    starred?: boolean;
    status: TFlowStatus;
    submitted_by_user?: { first_name?: string; last_name?: string };
    submitted_on: string;
    summary: IFlowSummary;
    flow_tags: TFlowTag[];
    theme: IFlowTheme;
    thumbnail_image?: IFlowThumbnail;
    title?: string;
    variables: IVariableResponse[];
    workspace_id?: string;
    card_on_file_enabled: boolean;
    public_configuration?: IFlowPublicConfiguration;
    flow_url?: string;
    view_only_flow_url?: string;
    flow_permissions?: IFlowPermission[];
    require_strong_auth?: boolean;
}

export interface IFixedFlowResponse
    extends Omit<SetOptional<IFlowResponse, 'last_action_number'>, 'actions'> {}

export interface IFlowVariablesResponse {
    variables: IVariableResponse[];
}

export interface IConnectedWorkflowsResponse {
    workflows: IWorkflowResponse[];
}

export interface IFlowRevisionsFromBackend {
    revisions: IFlowRevision[];
}

export interface IWorkflowResponse {
    _id: string;
    title: string;
}

export interface IFlowRevision {
    _id: string;
    status: TFlowStatus;
    version_number: number;
    published_at?: string;
    updated_at: string;
    submitted_on?: string;
    submitted_by_user?: {
        first_name?: string;
        last_name?: string;
        _id?: string;
    };
    last_content_edit: {
        user_full_name: string;
        user_id: string;
    };
    published_by_user?: {
        full_name: string;
        _id: string;
    };
}

export enum ClientActions {
    clientPaid = 'client_paid',
    clientAnsweredQuestions = 'client_answered_questions',
    clientSelectedServices = 'client_selected_services',
    clientSigned = 'client_signed'
}

export interface IFlowPrePublishResponse {
    diverged_variables: IVariableResponse[];
    diverged_actions: ClientActions[];
}

export interface IMinimalFlowExpiration {
    expired: boolean;
    will_expire_at: Date;
    expired_at: Date;
    message: string;
}

export interface IFlowMinimalResponse {
    branding_data: ICompanyBrandingData;
    client: IClientMinimalData;
    expiration: IMinimalFlowExpiration;
    owner: IUserBrandingData;
    require_authentication: boolean;
    theme: IFlowTheme;
    title: string;
    card_on_file_enabled: boolean;
    client_facing_host: string;
    is_client_portal_exposed: boolean;
}

export enum FlowApplyActionsErrors {
    actionResolveFailed = 'HBFailedToApplyFlowActionsError',
    flowGotCorrupted = 'HBActionsCausedFlowCorruptions',
    flowNotSynced = 'HBNotSyncedFlowAction',
    flowNotSyncedWithoutWebSockets = 'HBNotSyncedFlowActionWithoutWebSockets',
    invalidParent = 'HBInvalidBlockParentError',
    noPermission = 'HBNoPermissionOnFlowError',
    cannotDeleteFlow = 'HBCannotDeleteFlowError',
    flowNotInControl = 'HBUserNotInControlOfFlow',
    tempAccessTriedGetControlFromVendor = 'HBTempUserTriedAccessFlowInControlByVendor'
}

export enum FlowApplyActionsErrorsTypes {
    cardOnFileError = 'CardOnFileError'
}

export enum FlowApplyActionResponseStatus {
    success = 'success',
    failed = 'failed',
    notReached = 'not_reached'
}

export interface IActionResult {
    status: FlowApplyActionResponseStatus;
    action_number: number;

    [key: string]: any;
}

export interface IFlowApplyActionResponse {
    actions: IActionResult[];
    blocks?: TBlockResponse[];
    pages?: IPageResponse[];
    variables?: IVariableResponse[];
    last_action_number?: number;
    last_changed_at?: string;
    last_content_edit?: ILastContentEdit;
    revision_id?: string;
    status?: TFlowStatus;
    revision_count?: number;
    require_strong_auth?: boolean;
}

export interface IFlowUpdatedResponse
    extends Pick<
        IFlowApplyActionResponse,
        'pages' | 'blocks' | 'variables' | 'status'
    > {
    status?: TFlowStatus;
    is_eligible_for_mark_as_completed?: boolean;
}

// export interface IConfigurationResponse extends IConfiguration {}
//     api_urls_prefix: string;

export interface IHbApiHeaders {
    admin_login: string;
    admin_token: string;
    api_client_version: string;
    auth_token: string;
    auth_user_id: string;
    csrf_token: string;
    custom_domains_signature: string;
    vendor_login: string;
    data_science_service: string;
    delete_user_token: string;
    duplicate_calls_prevention: string;
    finance_app_signature: string;
    fingerprint: string;
    force_tos_version: string;
    hb_referer: string;
    is_temp_pass: string;
    last_user_update: string;
    latest_tos_version: string;
    mfa_session: string;
    mta_uuid: string;
    platform: string;
    scheduling_engine: string;
    signature: string;
    tos_update_needed: string;
    trusted_device: string;
    user_tos_version: string;
    view_only_hash: string;
    weak_auth_hash: string;
    weak_auth_user_email: string;
    weak_auth_user_id: string;
}

export type Environment = 'production' | 'staging' | 'development' | 'test';

export interface IConfiguration {
    // Runtime Config
    finance_tax_year?: string;
    api_updateURLs_prefix?: string;
    api_urls_prefix?: string;
    api_version_force_reload_diff?: number;
    api_version?: number;
    aws_region?: string;
    aws_bucket?: string;
    aws_access_key?: string;
    aws_policy?: string;
    aws_policy_expiration?: string;
    aws_signature?: string;
    environment?: Environment;
    facebook_app_id?: string;
    facebook_app_updateURL?: null;
    finance_app_url?: string;
    forter_site_id?: string;
    google_api_api_key?: string;
    google_api_client_id?: string;
    google_recaptcha_site_key?: string;
    google_recaptcha_v3_site_key?: string;
    hb_api_headers?: IHbApiHeaders;
    hb_base_error_codes?: any;
    onboarding_enums?: Record<number, string>;
    show_honey_tips?: boolean;
    stripe_key?: string;
    stripe_test_key?: string;
    updateURL?: string;
    url?: string;
    version?: string;
    websocket_address_2?: string;
    websocket_v3_address?: string;
    websocket_v3_transports?: string[];
    web_socket_conn_retry?: boolean;
    activate_recaptcha?: boolean;
    partnerstack_coupon?: string;
    flow_gallery_url?: string;
    company_type_enum?: ICompanyTypeEnum[];
    company_sub_types_enum?: CompanySubTypeList[];
    company_type_to_service_list?: Record<string, ISuggestedServiceList[]>;
    facebook_pixel_id?: string;
    partnerstack_public_key?: string;
    e2e_env?: string;
    firebase_config?: FirebaseOptions;
    scheduling_engine_url?: string;
    block_mixpanel?: boolean;
    block_mixpanel_react?: boolean;
    time_to_upload_materials?: string;
    statsig_client_api_key?: string;
    local_user?: string;
    staging_env_name?: string;
    non_event_company_type_enum?: string[];
    force_bypass_blocking_modals?: boolean;
    statsig_e2e_tier?: string;
    statsig_api_url?: string;
    lead_forms_public_attachments_folder?: string;
    recurly_api_key?: string;
    statsig_log_init_timeout_percentage?: number;
    statsig_initialization_timeout?: number;
    own_id_app_id?: string;
    prismm_public_key?: string;
    facebook_test_event_code?: string;
    enable_help_menu_in_dev?: string;
    otam_postponing_day_cap?: string;
    google_api_custom_domain_api_key?: string;
    firebase_api_key?: string;
    branch_api_key?: string;
    live_feed_channels?: string;
    intercom_app_id?: string;
    upscope_key?: string;
    vapid_public_key?: number[];
    should_use_axios?: string;
    web_socket_environment?: string;
    instagram_app_client_id?: string;
    is_aws_server?: string;
    user_registration_2fa_delay_in_hours?: string;
    doodle?: string;
    auto_coupon?: string;
    user_templates?: string;
    packaging_features?: string;
    pricing_plans?: string;
    enable_segment?: string;
    router_app_segment_write_key?: string;
    api_cache_config?: string;
    client_cache_enabled?: string;
    client_cache_version?: string;
    t1_excluded_referral_campaigns_uid?: string;
    // Build Config
    build_environment?: Environment;
    powered_by_launch_date?: string;
    should_show_powered_by_for_clients?: boolean;
}

export interface ISuggestedServiceList {
    key: string;
    name: string;
    selected: boolean;
}

export type CompanyTypesWithSubTypes = Extract<
    TCompanyTypes,
    'coaching' | 'business_consultant'
>;

interface CompanySubTypeList {
    type: CompanyTypesWithSubTypes;
    sub_types: CompanySubType[];
}

type CompanyConsultingSubType =
    | 'business_consulting'
    | 'marketing_consulting'
    | 'strategy_consulting'
    | 'financial_consulting'
    | 'operations_consulting'
    | 'human_resources_consulting'
    | 'other';

type CompanyCoachingSubType =
    | 'business_coaching'
    | 'financial_coaching'
    | 'health_wellness_coaching'
    | 'career_coaching'
    | 'life_coaching'
    | 'other';

type CompanySubType = CompanyConsultingSubType | CompanyCoachingSubType;

export interface ICompanyType {
    label: string;
    value: CompanyTypeKeyEnum;
    parentType: string;
    isActive?: boolean;
}

export interface ICompanyTypeEnum {
    key: TCompanyTypeKeyEnum;
    text: string;
    textInformal: string;
    icon: string;
    order: number;
    new_msp?: boolean;
    old_msp?: boolean;
}

export type TLimitedPeriodUnit = 'year' | 'month' | 'day';

export type TDiscountType = 'percent' | 'fixed';

export interface ICouponResponse {
    code: string;
    discount_amount: number;
    discount_percent: number;
    discount_type: TDiscountType;
    duration: string;
    name: string;
    referrer?: {
        email: string;
        first_name: string;
        last_name: string;
        _id: string;
    };
    state?: string;
    temporal_amount?: number;
    temporal_unit?: TLimitedPeriodUnit;
}

export interface IUpdateImageResponse {
    cover_images: IImage[];
    icon_image: IImage;
}

export enum PublishFlowReason {
    PublishWithoutShare = 'publish_without_share'
}

export enum PublicPendingTaskStatus {
    Pending = 0,
    Finished = 1,
    Failed = 2,
    Aborted = 3
}

export interface ICreateClientPendingTaskResponse {
    task_id: string;
}

export interface ICheckClientPendingTask<
    T extends ClientPendingTaskType = ClientPendingTaskType
> {
    _id: string;
    pending_task_error_message: string;
    pending_task_progress: Parameters<
        ClientPendingTask[T]['onUpdateProgress']
    >[0];
    pending_task_result: ClientPendingTask[T]['returnType'];
    pending_task_state: 'pending' | 'started' | 'finished' | 'aborted';
    pending_task_state_cd: PendingTaskStatus;
    task_type: T;
}

export type TCheckClientPendingTaskResponse = Array<ICheckClientPendingTask>;

export interface IPublicPendingTaskNode {
    canceled: boolean;
    pollCount: number;
    promise: Promise<unknown>;
    reject: (reason?: any) => void;
    resolve: (value: unknown) => void;
    taskData: Record<string, any>;
    taskId: string;
    fetchResultsHook: (token: string) => any;
}

export interface ICompanyEmailTemplatesResponse {
    _id: string;
    vendor_emails: IVendorEmail[];
}

export const TLeadSource = z.object({
    editable: z.boolean(),
    label: z.string(),
    open_text: z.boolean().optional().nullable(),
    _id: z.string()
});

export type ILeadSource = z.infer<typeof TLeadSource>;

export interface IProjectType {
    editable: boolean;
    label: string;
    _id: string;
}

export const PROJECT_TYPES_COUNT_MAX = 18;

export interface IESPackageResponse {
    package_id: string;
    name: string;
    updated_at: string;
    sent_on?: string;
    source: string; // file / flow
    source_id: string;
    source_title: string;
    flow_content_id: string;
    block_id?: string;
    quantity?: number;
    unit?: ServiceItemUnit;
    price_per_unit: number;
    items_count: number;
    type: ServiceItemType.Package;
    description: string;
    package_image?: IImage;
    service_asset?: string;
    tax_1?: boolean;
    tax_2?: boolean;
    tax_3?: boolean;
    project_name?: string;
    id?: string;
    qb_item_id?: string;
}

export interface IESServiceResponse {
    unit?: ServiceItemUnit;
    quantity?: number;
    price: number;
    name: string;
    updated_at: string;
    sent_on: string;
    source: string; // file / flow
    source_id: string;
    source_title: string;
    description: string;
    price_per_unit?: number;
    image_id?: string;
    service_asset?: string;
    tax_1: boolean;
    tax_2: boolean;
    tax_3: boolean;
    type: ServiceItemType.Service;
    package_service_image?: IImage;
    project_name?: string;
    block_id: string;
    id?: string;
    qb_item_id?: string;
}

export type TESServiceOrPackage = IESPackageResponse | IESServiceResponse;

export interface IFrequentServiceOrPackagesResponse {
    results: TESServiceOrPackage[];
    search_term?: string;
}

export interface IUnifiedServiceOrPackagesResponse {
    results: TESServiceOrPackage[];
    templates: IESPackageResponse[];
    search_term?: string;
}

export interface IPackagesAndServicesListResponse {
    services: IESServiceResponse[];
    packages: IESPackageResponse[];
    templates: IESPackageResponse[];
    search_term?: string;
}

interface IBaseServiceResponse {
    _id: string;
    description: string;
    price_per_unit?: number;
    unit?: ServiceItemUnit;
    taxable?: boolean;
    svcable?: boolean;
    tax_1?: boolean;
    tax_2?: boolean;
    tax_3?: boolean;
    quantity?: number;
    order?: number;
    qb_item_id?: string;
    selected?: boolean;
}

export interface IPackageResponse extends IBaseServiceResponse {
    title: string;
    base_price: number;
    image_url?: any;
    origin_copy_id: string;
    quantity?: number;
    base_services: IServiceResponse[];
    total_price: number;
    package_image?: IImage;
}

export interface IServiceResponse extends IBaseServiceResponse {
    name: string;
    category: string;
    price: number;
    discount: number;
    discount_type?: any;
    package_service_image: IImage;
    service_asset?: string;
}

export interface IFetchPackageResponse {
    _id: string;
    vendor_packages: IPackageResponse[];
}

export interface IPaymentMethodResponse {
    _id: string;
    name: string;
    last4: string;
    _type: PaymentMethodType;
    type: CreditCardProcessor;
    is_active: boolean;
    card_role?: any; // TODO
    paid_by: string;
    user_id: string;
    bank_role: string[]; // TODO type
    account_name?: string;
    account_sub_type?: string;
    bank_name?: string;
    has_ssn: boolean;
    has_tax_id: boolean;
    routing_number_last_4?: string;
    tax_id?: string;
    account_number_last4?: string;
    is_mobile_wallet: boolean;
    payment_method_type: TPaymentMethod;
    created_at: string;
}

export interface IStripePerson {
    _id: string;
    first_name: string;
    last_name: string;
    date_of_birth: string;
    ssn: string;
    ownership_percentage: number;
    street_address: string;
    city: string;
    state: string;
    zip_code: string;
    email: string;
    phone_number: string;
    token_id: string;
    representative: boolean;
    owner: boolean;
    job_title: string;
    full_name: string;
    has_identification_front_image: boolean;
    has_identification_back_image: boolean;
    info_missing: boolean;
    fields_to_fix: string[];
}

export type InstantDepositFailReason =
    | 'card_expired'
    | 'card_invalid'
    | 'card_lost'
    | 'card_ineligible'
    | 'no_debit_card'
    | 'bank_decline'
    | 'pending_verification'
    | 'open_dispute'
    | 'other';

export interface IStripeBankAccount extends IPaymentMethod {
    _type: 'PaymentMethodStripeBankAccount';
    fields_to_fix?: string[];
    is_bank_account_instant_eligible?: boolean;
    is_instant_deposit_to_bank_account_enabled?: boolean;
    last_instant_deposit_fail_reason?: InstantDepositFailReason;
}

export type AccountSetupStage = 'not_ready' | 'ready_to_start' | 'completed';

export type TWaitlistableFeature = 'automations_v2';

export interface IAccountResponse {
    _id: string;
    account_settings: IAccountSettings;
    account_setup_stage?: AccountSetupStage;
    block_files_flag?: string;
    card_on_file_enabled: boolean;
    enable_flow_flag?: string;
    finance_account_status?: FinanceAccountStatus;
    has_multiple_brands: boolean;
    has_selected_next_plan?: boolean;
    has_subscriptions: boolean;
    is_subscribed: boolean;
    otam_group?: string;
    predicted_member_segment_measurement: string;
    pricing_plan: IPricingPlan | null;
    selected_next_plan_code?: TPlanCode;
    subscription: ISubscription | null;
    subscription_paused_date: string;
    trial: ITrial;
    automations_mode: TAutomationsMode;
    predicted_member_segment?: string;
}

export type TAutomationsMode =
    | 'old'
    | 'explore_new'
    | 'activated_new'
    | 'reverted'
    | 'new';

export interface IImportTemplateResult {
    imported_shared_template_id?: string;
    error?: string;
}

export type TPricingPlanForAccountResponse = Exclude<
    IAccountResponse,
    'account_settings' | 'subscription' | 'trial'
>;

export interface IMinCompanyResponse {
    _id: string;
    twitter_url?: string;
    facebook_url?: string;
    instagram_url?: string;
    pinterest_url?: string;
    linkedin_url?: string;
    behance_url?: string;
    dribbble_url?: string;
    tiktok_url?: string;
    other_url?: string;
    company_name?: string;
    icon_image?: IImage;
    company_fonts?: ICompanyFont[];
}

export interface IUserPermissions {
    user_id: string;
    company_permission_cd: number;
    company_permission: TeamRole;
}

export interface IGoogleIntegration {
    google_integration_permissions: Array<{
        integration_type: TGoogleIntegrationType;
        status: 'active' | 'revoked';
    }>;
    google_account_email?: string;
}

export interface IReferralUserStatsResponse {
    commission_sum: number;
    invited_count: number;
    member_count: number;
    signed_up_count: number;
    total_count: number;
    commission_sum_curr_year?: number;
    member_count_curr_year?: number;
    eligible_count_curr_year?: number;
    signed_up_count_curr_year?: number;
}

export type TReferralsBonusMilestone = {
    min_referral_count: number;
    bonus_value: number;
};

export interface IReferralUserBonusResponse {
    current_count: number;
    bonus_structure: TReferralsBonusMilestone[];
}

export type TCompanyVertical = 'consultant';

export interface ICompanyResponse extends IMinCompanyResponse {
    agreements?: IAgreement[];
    all_inclusive_instant_payouts?: boolean;
    allow_ach_refund?: boolean;
    allow_client_ach?: boolean;
    archive_reasons?: string[];
    archived?: boolean;
    bank_account_payment_method_id?: string;
    bank_account_user_id?: string;
    block_templates?: IFlowTemplate[];
    blog_url?: string;
    brand_color?: string;
    brochure_containers?: [];
    brochure_templates?: IFileTemplate[];
    city?: string;
    client_portal_configuration?: IClientPortalConfiguration;
    company_assets?: IFileAsset[];
    company_flow_theme?: IFlowTheme;
    company_owner_notifications: ICompanyOwnerNotification[];
    company_settings: ICompanySettings;
    company_type?: TCompanyTypes;
    company_url_domain?: string;
    company_url_prefixes?: Array<string>;
    company_vertical?: TCompanyVertical;
    contact_forms?: IContactForm[];
    cover_images?: IImage[];
    custom_contact_fields?: ICustomFieldSchema[];
    custom_project_fields?: ICustomProjectField[];
    custom_project_fields_v2?: ICustomFieldSchema[];
    ai_contact_followup_suggestions_enabled?: boolean;
    default_cover_image_id: string;
    default_project_type_label?: string;
    default_timezone?: TShortTimezone;
    description?: string;
    eligible_for_finance_app_payments?: boolean;
    eligible_for_instant_payouts?: boolean;
    email?: string;
    flows?: IFlowTemplate[];
    folders?: IFolder[];
    has_prismm_integration?: boolean;
    has_pictime_integration?: boolean;
    hidden_project_fields?: string[] | null;
    invoices?: IInvoice[];
    is_flow_thumbnail_visible_to_client?: boolean;
    is_msp?: boolean;
    is_powered_by_hb?: boolean;
    lead_sources?: ILeadSource[];
    logo_image?: IImage;
    n8n_user_initialized: boolean;
    owner?: IOwner;
    phone_number?: string;
    pipeline_views?: TPipelineView[];
    project_types?: IProjectType[];
    proposals?: IProposal[];
    questionnaires?: IFileTemplate[];
    quickbooks_integration?: IQuickbooksIntegration;
    scheduler_url?: string;
    schema_limit_per_entity?: number;
    service_units?: string[];
    sms_enabled?: boolean;
    sms_payment_reminders_enabled?: boolean;
    sms_meeting_reminders_enabled?: boolean;
    client_2fa_sms_enabled?: boolean;
    street_address?: string;
    subscribed?: boolean;
    subscribed_for_free?: boolean;
    tags: ITag[];
    team_members?: ITeamMember[];
    text_color?: string;
    user_permissions?: IUserPermissions[];
    user_pipeline_stages?: IUserPipelineStageResponse[];
    vendor_packages?: IVendorPackage[];
    vendor_emails?: IVendorEmail[];
    website_url?: string;
    workflow_automation_infos?: IWorkflowAutomationInfo[];
    zip_code?: string;
    company_spaces?: TCompanySpaceUpdate[];
    contact_enrichment_enabled?: boolean;
    company_custom_domains?: Array<CompanyCustomDomainResponse>;
    company_type_name?: string;
    account?: IAccountResponse;
    default_is_client_portal_exposed?: boolean;
    timeline_containers?: any[];
    show_membership_page?: boolean;
}

export interface ICompanySmsPreferences {
    sms_enabled: boolean;
    sms_payment_reminders_enabled: boolean;
    sms_meeting_reminders_enabled: boolean;
    client_2fa_sms_enabled: boolean;
}

export const COMPANY_OWNER_NOTIFICATION_TYPE = {
    NOTIFY_CLIENT_PAID_TEAM_MEMBER: 'notify_client_paid_team_member',
    NOTIFY_CLIENT_SIGN_TEAM_MEMBER_FILE: 'notify_client_sign_team_member_file'
} as const;
export const COMPANY_OWNER_NOTIFICATION_TYPE_TO_CD_MAP = {
    [COMPANY_OWNER_NOTIFICATION_TYPE.NOTIFY_CLIENT_PAID_TEAM_MEMBER]: 0,
    [COMPANY_OWNER_NOTIFICATION_TYPE.NOTIFY_CLIENT_SIGN_TEAM_MEMBER_FILE]: 1
} as const;

export type TCompanyOwnerNotificationType =
    (typeof COMPANY_OWNER_NOTIFICATION_TYPE)[keyof typeof COMPANY_OWNER_NOTIFICATION_TYPE];

export interface ICompanyOwnerNotification {
    _id: string;
    notification_type: TCompanyOwnerNotificationType;
    notification_type_cd: number;
    is_notification_on: boolean;
}

export interface ICompanySettings {
    _id: string;
    share_team_members_calendar: boolean;
}

export type CUSTOM_DOMAINS_VALIDATION_STATUS_NON_RETRYABLE =
    | 'active'
    | 'inprogress';
export type CUSTOM_DOMAINS_VALIDATION_STATUS_RETRYABLE =
    | 'invalid'
    | 'inactive'
    | 'cname_failed';
export type CUSTOM_DOMAINS_VALIDATION_STATUS =
    | CUSTOM_DOMAINS_VALIDATION_STATUS_RETRYABLE
    | CUSTOM_DOMAINS_VALIDATION_STATUS_NON_RETRYABLE;

export type CompanyCustomDomainResponse = {
    full_hostname: string;
    domain: string;
    subdomain: string;
    validation_status: CUSTOM_DOMAINS_VALIDATION_STATUS;
};

type TDevice = 'desktop' | 'mobile' | 'ios' | 'android';

export interface IOnboardingContainer {
    ab_test_show_progress_bar: boolean;
    event_id: string;
    onboarding_active: boolean;
    onboarding_completed: boolean;
    onboarding_entry_point: string | null;
    onboarding_invoice_template_id: string | null;
    onboarding_proposal_template_id: string | null;
    onboarding_start_device: TDevice | null;
    onboarding_step: string;
    onboarding_version: number;
    workspace_file_id: string | null;
    workspace_id: string | null;
}

export interface ISetActiveEmailProviderRequest {
    type: 'gmail' | 'nylas';
    source?: 'pipeline' | 'workspace' | 'contactView' | 'setup';
}

export interface ISetActiveEmailProviderResponse {
    selected_provider: TEmailProvider;
}

export interface IUserResponse {
    _id: string;
    first_name?: string;
    last_name?: string;
    full_name: string;
    address?: string;
    is_bookkeeper?: boolean;
    workspaces_count?: number;
    email: string;
    email_verified?: boolean;
    is_vendor?: boolean;
    bookkeeperModeCompanyId?: string;
    is_password_exposed?: boolean;
    is_email_deliverable?: boolean;
    api_version: string;
    authentication_token: string;
    audited_companies?: IAuditeeCompany[];
    email_signatures: Array<IEmailSignature>;
    company?: ICompanyResponse;
    default_flow_signature?: {
        signature: string;
        signature_type: string;
    };
    features_variations?: Partial<Record<RolloutFeatureFlags, string>>;
    features_active?: Array<any>;
    twitter_url?: string;
    facebook_url?: string;
    instagram_url?: string;
    pinterest_url?: string;
    disable_client_pvl_requests?: boolean;
    initials?: string;
    profile_image?: IImage;
    system_user_type?: SystemUserType;
    recurly_key?: string;
    account_id?: string;
    referral_container?: {
        referred_by_code?: string;
        pending_referral_code: string;
    };
    account?: IAccountResponse;
    plaid_env?: string;
    plaid_public_key?: string;
    gift_card_feature_allowed?: boolean;
    ab_tests?: Partial<IABTests>;
    user_ui_persistences?: {
        key: string;
        value: Record<string, any>;
    }[];
    first_login?: string;
    first_time_on_chrome_extension?: string;
    first_time_sent_flow_on?: string;
    first_time_sent_flow_test_on?: string;
    intercom_hmac?: string;
    is_pro?: boolean;
    is_educator?: boolean;
    is_activated?: boolean;
    is_active?: boolean;
    is_account_owner?: boolean;
    statsig_stable_id?: string;
    user_intent?: TIntentType;
    is_temp_pass_login?: boolean;
    trusted_device?: string;
    profile_image_url?: string;
    created_at?: string;
    website_url?: string;
    hb_user_type?: HbUserType[];
    activated_on?: string;
    payment_methods?: IPaymentMethod[];
    social_network_accounts?: SocialNetworkAccount[];
    is_company_owner?: boolean;
    attribution?: IAttribution;
    user_company_permission?: IUserPermissions;
    ooo_in_effect?: boolean;
    phone_number?: string;
    login_2fa_enabled?: boolean;
    has_password?: boolean;
    phone_number_for_two_factor_auth?: string;
    last_login_time?: string;
    imported_template_result?: IImportTemplateResult;
    nylas_calendar_feature_allowed?: boolean;
    gmail_integration_revoked_at?: string;
    is_hb_admin?: boolean;
    default_auto_expiration_period?: string | null;
    leads_capture_config?: {
        enabled: boolean;
        is_eligible: boolean;
    };
    calendar_colors?: ICalendarColors;
    hb_location?: {
        country_code: string;
    };
    has_canva_integration?: boolean;
    has_logged_in_via_desktop?: boolean;
    onboarding_container?: Partial<IOnboardingContainer>;
    atws_status?: IAtwsStatus;
    job_title?: string;
    company_role?: string;
    user_type_cd?: number;
    user_type?: string;
    user_type_name?: UserTypeDisplayName;
    google_integrations?: IGoogleIntegration[];
    calendly_url?: string;
    is_real_chargeable_user?: boolean;
    password?: string;
}

export interface IAngularUser extends IUserResponse {
    bookkeeperModeCompanyId?: string;
    bookkeeperModeCompany?: IAuditeeCompany;
}

export type TAccessREquestType = 'pro_user' | 'bookkeeper';

export interface IAccessRequestResponse {
    status: string;
    _id: string;
    expires_at: Date | null;
}

export interface IResetInstantBrandingElementsResponse {
    reset_properties: ISuggestedAssets;
}

export interface ISuggestedAssets {
    profile_image?: IImage;
    icon_image?: IImage;
    brand_color?: string;
    brand_colors?: string[];
}

export interface IBookkeeperAccessRequestResponse
    extends IAccessRequestResponse {
    company: IAuditeeCompany;
    bookkeeper_user?: IUserPublicResponse;
}

export interface IProAccessRequestResponse extends IAccessRequestResponse {
    user: IUserResponse;
    pro_user: IUserResponse;
}

export interface IProAccessResponse {
    results: IProAccessRequestResponse[];
    total_results: number;
    statuses_count: IAccessRequestCounts;
}

export interface IParsedProAccessResponse extends IProAccessResponse {
    results: ProAccountAccessAuthorization[];
}

export interface IBookkeeperAccessResponse {
    results: IBookkeeperAccessRequestResponse[];
}

export interface IInviteBookkeeperData {
    full_name: string;
    email: string;
    job: string;
    phone_number: string;
    address: string;
    userId: string;
    company_name: string;
    system_user_type: SystemUserType;
}

export interface IInviteBookkeeperDataForServer
    extends Omit<IInviteBookkeeperData, 'company_name'> {
    company: { company_name: string };
}

export interface IInvoiceResponse {
    _id: string;
    title: string;
    bill_to_user: IUserResponse;
    discount: number;
    discount_type: DiscountTypes;
    tax_1?: ITaxResponse;
    tax_2?: ITaxResponse;
    tax_3?: ITaxResponse;
}

export interface ITaxResponse {
    label: string;
    value: number;
    type: TaxType;
    tax_id: string;
    active: boolean;
}

export enum ChargeDescription {
    Cash = 'cash',
    Check = 'check',
    Other = 'other'
}

export interface IPaymentVariablePendingMarkAsPaid {
    charge_date: string;
    charge_description: string;
    charge_notes?: string;
    source?: 'payment_canvas_dots_menu' | 'builder' | 'responses';
}

export interface IRequestPayment {
    flowId: string;
    paymentId: string;
    email: IVendorEmail;
}

export interface GetRefundDataRequest {
    payment_id: string;
    file_id?: string;
}

export interface IRefundData {
    client_info?: {
        first_name?: string;
        full_name?: string;
        last4?: string;
        last_name?: string;
        payment_type?: string;
        profile_image?: IImage;
    };
    vendor_info?: IPaymentMethodResponse & {
        use_finance_app?: boolean;
        finance_app_payment_method: IPaymentMethodResponse;
    };
    is_client_pending_refund?: boolean;
    refund_transaction_id?: string;
}

export interface IBankInstantDepositEligibilityResponse {
    is_bank_account_instant_eligible: boolean;
    is_bank_account_instant_enabled: boolean;
}

export interface IPaymentVariablePendingMarkAsRefunded {
    expected_to_be_charged: boolean;
    refunded_amount?: number;
    refunding_user_id: string;
    charge_description: string;
    payment_method_id?: string;
    refund_notes?: string;
}

export interface IMarkPaymentAsInstantPayoutResponse {
    use_instant_payout: boolean;
    toggle_success: true;
    company_all_inclusive_instant_payouts: boolean;
    adopted_instant_payouts: boolean;
}

export interface IPaymentVariableRefunded {
    flow_id: string;
    workspace_file_id?: string;
    refund_payment_method_id: string;
    payment_id: string;
    amount_to_refund: number;
    expected_to_be_charged: boolean;
}

export type TPaymentStatusCategory =
    | 'idle'
    | 'in_progress'
    | 'action_needed'
    | 'credited'
    | 'lost';

export type TPaymentStatus =
    | 'canceled'
    | 'upcoming'
    | 'due'
    | 'overdue'
    | 'payment_failed'
    | 'charged'
    | 'pending_confirmation'
    | 'bank_approved'
    | 'processing'
    | 'processed'
    | 'deposited'
    | 'payout_failed'
    | 'mark_as_paid'
    | 'refund_initiated'
    | 'refund_in_progress'
    | 'refund_error'
    | 'partially_refunded'
    | 'fully_refunded'
    | 'dispute_initiated'
    | 'dispute_pending'
    | 'covered_dispute'
    | 'dispute_on_hold'
    | 'held_for_dispute'
    | 'dispute_won'
    | 'dispute_lost'
    | 'dispute_partially_won';

export interface IPaymentStatus {
    status: TPaymentStatus;
    status_category: TPaymentStatusCategory;
    amount?: number;
    total_refunded_amount?: number;
    date?: Date;
    estimated_payout_date?: Date;
    last_reminder?: Date;
    pending_dispute_deadline?: Date;
    expected_resolution_date?: Date;
}

export interface IMinimalPaymentVariableValue {
    is_tbd: boolean;
    due_date?: any;
}

export interface IPaymentVariableValue extends IMinimalPaymentVariableValue {
    auto_payment_reminder_sent_on?: string;
    charge_date?: string;
    last_refund_timestamp?: string;
    charge_id?: string;
    amount: number;
    amount_paid: number;
    charge_statement_descriptor: string;
    is_paid?: any;
    is_manual?: boolean;
    is_auto_paid?: boolean;
    is_pending?: any;
    is_ach?: boolean;
    is_stripe_deposit_date?: boolean;
    is_instant_deposit?: boolean;
    instant_payout_theoretical_fee?: number;
    allow_instant_payout?: boolean;
    invoice_id?: any;
    invoice_number?: string;
    invoice?: string;
    tip_paid: number;
    is_refunded?: boolean;
    refunded_amount: number;
    refund_data?: IRefundData;
    payment_id?: any;
    pending_mark_as_paid: IPaymentVariablePendingMarkAsPaid;
    pending_mark_as_refunded: IPaymentVariablePendingMarkAsRefunded;
    pending_undo_mark_as_paid: boolean;
    payout_arrival_date?: string;
    reminders?: IReminder[];
    transferred_to_vendor?: boolean;
    payment_payer_email: string;
    payment_payer_name: string;
    payment_method_name: string;
    payment_method_type: PaymentMethodType;
    payment_method_type_name: string; // Visa, Discover...
    payment_method_last4: string;
    payment_method_id: string;
    payment_index: number;
    payment_status_label: PaymentStatus;
    hb_transaction_fee_amount: number;
    discount_amount: number;
    milestone_completed_on?: string;
    charge_notes?: string;
    refund_notes?: string;
    currency?: Currency;
    amount_to_refund?: number;
    estimated_deposit_date?: string;
    payment_action_status?: any;
    invoice_header_snapshot?: IInvoiceHeaderSnapshot;
    late_fee_waived_ts?: Date;
    late_fee_paid?: number;
    payment_status_tracking?: IPaymentStatus[];
}

export interface IInvoiceHeaderSnapshot {
    invoice_number: string;
}

export enum AutoPayStatus {
    Inactive = 'inactive',
    Active = 'active',
    PendingApproval = 'pending_approval',
    Paused = 'paused'
}

export interface IChargeVariableValue {
    _type: 'Flow::Variable::ChargeVariable';
    auto_payment: {
        _id: string;
        activated_by: string;
        activated_by_vendor: boolean;
        paying_user_id: string;
        status: AutoPayStatus;
        canceled?: any;
    };
}

export interface ISelectedServiceVariableValue {
    service_item_block_id: string;
    user_id: string;
}

export interface IServiceSelectionVariableValue {
    selected_services: ISelectedServiceVariableValue[];
}

export interface IServiceSelectionItemVariableValue {
    quantity: number;
}

export interface IInvoiceSummaryVariableValue {
    sub_total: number;
    tax_1: number;
    tax_2: number;
    tax_3: number;
    total_discount: number;
    member_discount?: number;
    pay_in_full_discount?: number;
    total_price: number;
    total_tax: number;
    late_fee: number;
    number_of_payments?: number;
}

export interface IPaymentRecap {
    payment_block_id: string;
    due_date: any;
}

export interface ISignatureVariableValue {
    signature: string;
    signature_type?: SignatureTypes;
    signed_on: string;
}

export interface IReminder {
    _id: string;
    created_at: string;
    sent_on: string;
    updated_at: string;
}

export interface IPaymentVariableResponse {
    variable_id: string;
    connected_block_id: string;
    value: IPaymentVariableValue;
}

export interface IRequestPaymentResponse {
    paymentId: string;
    emailSubject: string;
    emailBody: string;
}

export interface IBankAccountResponse {
    id: string;
    type: string;
    subtype: string;
    mask: string;
    name: string;
    // taken from angular
    account_id: string;
    institution_id: string;
    institution_name: string;
    available: string;
    current: string;
    identity_names: string;
    primary_account: string;
    is_user_marked_as_business: string;
    refreshed_from_plaid_t: string;
    // not used anymore
    // account: string;
    // routing: string;
}

export interface IExchangePublicTokenResponse {
    request_id: string;
    accounts: IPlaidAccount[];
}

export interface IPlaidAccount {
    id: string;
    available?: string;
    account_id?: string; // same like id
    type: string;
    mask: string;
    name: string;
    subtype: string;
    institution_id: string;
    institution_name: string;
    verification_status: string;
}

export interface ICompanyThemeResponse {
    company_flow_theme: IFlowTheme;
}

export interface ICompanyDomainIsValidFlags {
    domain_exists: boolean;
    domain_whitelists_certificate_authority: boolean;
    is_domain_connect_supported: boolean;
}

export interface IConnectedCustomDomainResponse {
    is_valid: boolean;
    domain_connect_url?: string;
}

export enum Currency {
    USD = 'USD',
    CAD = 'CAD'
}

export enum RelationType {
    serviceSelectionDraftSource = 'service_selection_draft_source',
    serviceSelectionDraftInstance = 'service_selection_draft_instance'
}

export type TRequestStatus =
    | 'idle'
    | 'initial_load'
    | 'fetching'
    | 'loading'
    | 'succeeded'
    | 'failed'
    | 'getting_ui_persistence';

export interface IFlowTransformedData {
    flow: IFlow;
    pages: Record<string, ITransformedPageResponse>;
    blocks: Record<string, TBlockResponse>;
    variables: Record<string, IVariableResponse>;
}

export interface IEmailResponse {
    requested_email: IVendorEmail;
    vendor_emails: IVendorEmail[];
}

export type TEmailType =
    | 'agreement'
    | 'brochure'
    | 'client_file_not_viewed'
    | 'general'
    | 'invoice'
    | 'lead_thank_you'
    | 'pre_event_questionnaire'
    | 'proposal'
    | 'questionnaire'
    | 'timeline';

export interface IValidateEmailIntakeForm {
    is_valid: boolean;
    domain_not_valid: boolean;
    redirect_to_url: string;
}

export interface IGalleryTemplateRequestPayload
    extends SetRequired<IFlowGalleryTemplate, 'publisher_email'> {
    type: TemplateType.Flow;
    repositories: TRepoKey[];
}

export interface IOnboardingSequence<
    T extends IOnboardingStep = IOnboardingStep
> {
    steps: T[];
    current_step: T;
    is_sequence_complete: boolean;
    completion_percentage: number;
    is_clientflow_setup_guide?: boolean;
    is_contract_setup_guide?: boolean;
    is_setup_guide_for_nevs?: boolean;
    is_nev_contract_setup_guide?: boolean;
    is_ai_setup?: boolean;
}

export interface IOnboardingStep {
    key: OnboardingStepNames;
    status: 'pending' | 'complete';
    phase: 'wizard' | 'list'; // 'wizard' === onboarding/onboarding phase 1, 'list' === setup guide/onboarding phase 2
    score: number;
    order: number;
}

export interface IAIOnboardingStep extends IOnboardingStep {
    title: string;
    description: string;
}

export interface IRecentActivityResponse {
    recent_activities: IRecentActivityItem[];
    recent_activities_count: number;
}

export interface IRecentActivityItem {
    _id: string;
    type: TRecentActivityItemType;
    project_id: string;
    project_name: string;
    workspace_id: string;
    flow_id?: string;
    created_at: string;
}

export type TRecentActivityItemType =
    | 'inquiry'
    | 'feed_message'
    | 'flow_payment_paid'
    | 'flow_contract_signed'
    | 'payment_paid'
    | 'contract_signed'
    | 'smart_file_created'
    | 'legacy_file_created'
    | 'smart_file_sent'
    | 'legacy_file_sent'
    | 'smart_file_completed'
    | 'legacy_file_completed'
    | 'smart_file_viewed'
    | 'legacy_file_viewed'
    | 'session_scheduled';

export interface IWorkspaceCountsResponse {
    from_es: boolean;
    has_recent_activity: boolean;
    stages: Record<string, IPipelineStageMeta>;
    user_pipeline_stages: IPipelineStageMeta[];
    category_booked_counts: ICategoryCount;
    category_lead_counts: ICategoryCount;
    category_other_counts: ICategoryCount;
    counts: ICategoryCount;
}

export interface IWorkspaceMembersResponse {
    members: IMember[];
    _id: string;
}

export interface IWorkspaceMembersLiteResponse {
    _id: string;
    full_name: string;
    profile_image: IImage;
    company_id: string;
}

export interface WorkspaceNotificationResponse {
    message: string;
    file_id: string;
    file_owner_id: string;
    seen: boolean;
}

export interface WorkspacePipelineEventResponse {
    _id: string;
    event_budget?: number;
    event_date: string;
    event_details?: string;
    event_end_date: string;
    event_guests?: number;
    event_image?: IImage;
    event_location?: string;
    event_name: string;
    event_services?: string;
    event_source: WorkspaceSourceType;
    event_type: EventType;
    last_activity_date: string;
    lead_source: string;
}

export interface WorkspacePipelineStageResponse {
    _id: string;
    created_at: string;
    moved_on: string;
    stage_id: string;
    updated_at: string;
}

export interface WorkspacePipelineDataResponse {
    _id: string;
    blocked_date?: string;
    blocked_tag?: string;
    created_at: string;
    current_stage: SetOptional<
        WorkspacePipelineStageResponse,
        'created_at' | 'updated_at'
    >;
    stages_history: WorkspacePipelineStageResponse[];
    disable_tracking: boolean;
    tags: string[];
    updated_at: string;
    user_id: string;
}

export interface ICategoryCount {
    active: number;
    inactive: number;
    untracked: number;
}

export type TCampaignCalloutTypes = 'banner' | 'modal' | 'widget' | 'popover';
export type TCampaignPage = 'pipeline' | 'home';
export type TCalloutModalType = 'centered' | 'side_by_side';

export type ICampaignBanner = {
    callout_type: 'banner';
    type?: THBCalloutBannerType;
};
export type ICampaignWidget = {
    callout_type: 'widget';
    type?: HBCalloutCardProps['layout'];
};
export type ICampaignModal = {
    callout_type: 'modal';
    type?: TCalloutModalType;
};

export type ICampaignComponent = {
    title: string | undefined;
    description: string;
    cta_title: string;
    cta_link: string;
    image_link: string;
    open_in_new_tab: boolean;
    theme?: THBCalloutBannerTheme;
    size?: THBCalloutBannerSize;
    second_cta_link?: string;
    second_cta_title?: string;
    image_ratio?: THBCalloutBannerIllustrationAspectRatio;
} & (ICampaignBanner | ICampaignWidget | ICampaignModal);

export interface ICampaign {
    _id: string;
    product_page: TCampaignPage;
    campaign_component: ICampaignComponent;
}

export interface IUserPipelineStageResponse {
    _id?: string;
    isNew?: boolean;
    category?: PipelineStageCategory;
    is_editable?: boolean;
    name: string;
    oldStageName?: string;
    order?: number;
    type: PipelineAutomationType;
    types: PipelineAutomationType[];
    disabled_types: PipelineAutomationType[];
    clientId?: string;
    group?: PipelineStageGroup;
    workflows?: {
        triggers: {
            count: number;
            data: IAutomationInstance[];
        };
        actions: {
            count: number;
            data: IAutomationInstance[];
        };
    };
}

export interface IPipelineStageMeta {
    _id: string;
    type_cd: number;
    origin_type_cd: number;
    category_cd: number;
    name: string;
    updated_at: string;
    created_at: string;
    counts: ICategoryCount;
    order: number;
    category: 'booked' | 'lead' | 'other';
}

export interface IWorkspaceAdjustment {
    workspaces_on_stage_client_id: string;
    workspaces_on_stage_server_id: string;
    should_move_to_stage_client_id: string;
}

export interface IFetchPipelineRequest {
    userId?: string; // null means current user
    show_archive?: boolean;
    page?: number;
    per_page?: number;
    stage_id?: string;
    counts_only?: boolean;
    lean_format?: boolean;
    sort_desc?: boolean;
    allow_db?: boolean;
    sort_by?:
        | 'created_at'
        | 'event_date'
        | 'event_name'
        | 'stage'
        | 'stage_moved_on'
        | 'recent_activity'
        | 'lead_source'
        | 'project_type'
        | 'archive_reason';
}

export interface IPipelineStageResponse {
    page: number;
    last_page: boolean;
    from_es: boolean;
    data: IPipelineStageItem[];
}

export interface IProjectSearchResponseMember {
    full_name: string;
    email: string;
    initials: string;
    profile_image: IImage;
}

export interface IProjectSearchResponseStage {
    category: PipelineStageCategory;
    name: string;
    _id: string;
    disable_tracking: boolean;
}

export interface IProjectSearchResponseProperties {
    created_at: string;
    event_date: string;
    event_id: string;
    event_name: string;
    event_image?: IImage;
    inactive_users: string[];
    members: IProjectSearchResponseMember[];
    stage: IProjectSearchResponseStage;
    users_disabled_tracking: string[];
    workspace_id: string;
    last_activity_date?: string;
}

export interface IProjectSearchParams {
    enrich_workspaces?: boolean;
    page_size?: number;
}

export interface IProjectSearchResponse {
    properties: IProjectSearchResponseProperties;
    score: number;
    sortByType: number;
    type: 'Workspace';
    _id: string;
}

export interface IContactSearchResponse {
    properties: {
        email: string;
        full_name: string;
        contact_id: string;
        address?: string;
        custom_user_fields?: any[];
        last_name?: string;
        notes?: string;
        phone_number?: string;
        profile_image?: string;
        user_id?: string;
        vendor_id?: string;
    };
    score: number;
    type: string;
}

export interface IFilteredEventResponse {
    _id: string;
    event_name: string;
    event_date: string;
    last_activity_date: string;
    workspace_id: string;
    stage: IProjectSearchResponseStage;
}

export interface IFilteredEventsResponse {
    cur_page: number;
    data: IFilteredEventResponse[];
    last_page: boolean;
}

export type TFilteredEventsExtendedResponse = Omit<
    IFilteredEventsResponse,
    'cur_page'
> & {
    cur_page?: number;
    page?: number;
};

interface IPipelineStageItem {
    _id: string;
    active_state: boolean;
    event: {
        _id: string;
        event_date: string;
        event_name: string;
        last_activity_date: string;
        event_image: {
            cloudinary_public_id: string;
        };
    };
    workspace_pipeline_data: {
        disable_tracking: boolean;
        current_stage: {
            moved_on: string;
            stage_id: string;
        };
    };
}

export type TCompanyTypes =
    | 'accounting'
    | 'agency'
    | 'blogger'
    | 'business_consultant'
    | 'ai_consultant'
    | 'cake'
    | 'calligrapher'
    | 'caterer'
    | 'catering'
    | 'cinematography'
    | 'cleaning'
    | 'coaching'
    | 'contractor'
    | 'dj_musician'
    | 'doula'
    | 'education'
    | 'event_lighting'
    | 'event_planner'
    | 'fashion'
    | 'florist'
    | 'freelance'
    | 'graphic_designer'
    | 'hair_makeup'
    | 'interior_designer'
    | 'landscape'
    | 'maker_artisan'
    | 'marketing'
    | 'officiant'
    | 'other'
    | 'photo_booth'
    | 'photographer'
    | 'real_estate'
    | 'rental'
    | 'social_media'
    | 'stationery'
    | 'venue'
    | 'virtual_assistant'
    | 'home_staging'
    | 'visual_artist'
    | 'web_designer'
    | 'wellness'
    | 'marketing_consulting'
    | 'marketing_agency'
    | 'digital_marketing'
    | 'content_creation'
    | 'copywriting'
    | 'public_relations';

export type TServerImageCrop = Pick<
    IImage,
    'position_x' | 'position_y' | 'position_w' | 'position_h' | 'crop_ratio'
>;

export interface ICreateEmailData {
    encoded: boolean;
    file_attachments: IEmailAttachment[];
    html_body: string;
    image_attachments: IEmailAttachment[];
    save_as: boolean;
    subject: string;
    title: string;
}

export type TUpdateFlowConfiguration = PartialDeep<FlowConfigurationModel> & {
    public_flow_workflow_id?: string;
    lead_form_contact_tag_ids?: string[];
    lead_form_project_tag_ids?: string[];
    thumbnail?: IFlowThumbnail;
    title?: string;
};

export type TFlowType =
    | 'block_template'
    | 'flow_template'
    | 'flow_instance'
    | 'public_flow';

export enum TeamRole {
    Basic = 'basic',
    Moderator = 'moderator',
    Admin = 'admin',
    SuperAdmin = 'super_admin'
}

export enum TeamRoleToCd {
    basic,
    moderator,
    admin,
    super_admin
}

export enum SourceToCd {
    manual = 0,
    google = 1,
    widget = 2,
    companyTeam = 3,
    vendorReferral = 4,
    assignUser = 5,
    eventMember = 6,
    questionnaireAutomation = 7,
    brochure = 8,
    selfOnboarding = 9,
    groupWidget = 10,
    groupMember = 11,
    aisleSociety = 12,
    loverly = 13,
    mainStreetWeddings = 14,
    manualGroupLead = 15,
    vendorMobile = 16,
    malkutVenueOnboarding = 17,
    networkProject = 18,
    weddingSpot = 19,
    networkInvite = 20,
    pvlInquiry = 21,
    firstInvoice = 22,
    rtsSurvey = 23,
    communityLp = 24,
    workflowAutomation = 25,
    communityWidget = 26,
    zapier = 27,
    clientBot = 28,
    manualIosApp = 29,
    facebookLeadAds = 30,
    contactCsv = 31,
    firstProposal = 32,
    manualAndroidApp = 33,
    directLink = 34,
    scheduler = 35,
    hbChromeExtension = 36,
    agraba = 37,
    promotion = 38,
    giftCard = 39,
    preview = 40,
    onboarding = 41,
    flowAutomation = 42,
    sendTestTemplate = 43,
    sendTestSetupGuide = 44,
    sharedTemplate = 45,
    publicFlow = 46,
    awarenessDraft = 47,
    onboardingIosApp = 48,
    onboardingAndroidApp = 49,
    lifecycleMarketing = 50,
    setupGuide = 51,
    smartFileWizard = 52,
    scheduleIntentOnboarding = 53,
    leadCaptureIntentOnboarding = 54,
    publicTemplateSharing = 55,
    disputeInvoiceFlow = 56,
    importPdf = 57,
    aiTemplates = 58,
    automations = 59,
    projectsFile = 60,
    aiLeadSuggestion = 61,
    tkww = 62,
    calendly = 63,
    googleCalendar = 64
}

export interface IAddTeamMemberUserData {
    full_name: string;
    email: string;
    phone_number?: string;
    address?: string;
    company_permission: TeamRole;
    isMemberOfWorkspace: false;
    system_user_type: 'vendor';
}

export interface IInviteTeamMemberUserData {
    full_name: string;
    invitee_email: string;
    company_permission: TeamRole;
}

export type TFeaturePlanResponse = {
    feature_variations: TFeaturePlan;
    _id: string;
};

export type TEditTeamMemberRoleResponse = {
    user_permissions: IUserPermissions[];
    _id: string;
};

export interface IEmailDeliverabilityTestResponse {
    type: 'partial' | 'full';
    status: 'started' | 'failed' | 'done';
    created_at?: string;
    spf_syntax_validation_answer?: string;
    spf_syntax_validation_suggested_syntax?: string;
    spf?: string;
    test_errors: (
        | 'spf_record_not_found'
        | 'spf_invalid_syntax'
        | 'spf_unknown_issue'
        | 'spf_multiple_records'
        | 'dkim_invalid'
        | 'dkim_not_signed'
        | 'dmarc_record_not_found'
        | 'dmarc_invalid_syntax'
        | 'dmarc_invalid_version'
        | 'dmarc_invalid_policy'
    )[];
}

export interface ISubmitLeadFormTaskResponse {
    url: string;
    task_id: string;
}

export interface IPublicFlowSubmitResponse {
    variables: (
        | IVariableResponse<IScheduleContainerVariableValue>
        | IVariableResponse<IPaymentVariableValue>
    )[];
    blocks: TBlock[];
    new_flow_id: string;
    new_status: TFlowStatus;
    import_template_link?: string;
}

export interface ILeadFormCxExperienceResponse {
    new_nav_enabled: boolean;
}

export type TCloudinaryUploadResponse = Pick<
    // these are the actual fields we use from the Cloudinary response
    IUploadImageResponse,
    | 'secure_url'
    | 'public_id'
    | 'original_filename'
    | 'bytes'
    | 'width'
    | 'height'
    | 'phash'
>;

export interface IUploadImageResponse {
    asset_id: string;
    public_id: string;
    version: number;
    version_id: string;
    signature: string;
    width: number;
    height: number;
    format: 'jpg';
    resource_type: 'image';
    created_at: string;
    tags: string[];
    pages: number;
    bytes: number;
    type: 'upload';
    etag: string;
    placeholder: boolean;
    url: string;
    secure_url: string;
    folder: string;
    access_mode: 'public';
    existing: boolean;
    phash: string;
    original_filename: string;
    delete_token: string;
}

export type TImageUploadType =
    | 'company_file'
    | 'cover'
    | 'event_message'
    | 'icon'
    | 'logo'
    | 'profile_image'
    | 'project_gallery_image'
    | 'setup_asset'
    | 'user_identification_doc'
    | 'workspace_attachment'
    | 'workspace_message';

export interface ISaveContactRequest {
    _id?: string;
    first_name?: string;
    last_name?: string;
    full_name: string;
    email: string;
    job_title?: string;
    phone_number?: string;
    notes?: string;
    company?: {
        _id?: string;
        company_name?: string;
        company_type?: string;
        company_type_name?: string;
        address?: string;
        website_url?: string;
    };
    organization?: {
        name?: string;
    };
    website_url?: string;
    profile_image_url?: string;
    imported_from?: 'Google';
    address?: string;
    system_user_type?: 'client' | 'vendor';
    source?: string;
    custom_contact_fields?: Record<string, any>;
    interaction?: ILastInteraction;
}

export interface IEditContactRequest extends ISaveContactRequest {
    _id: string;
}

export interface ICreateEventWithClientRequest {
    email: string;
    full_name: string;
    phone_number?: string;
    address?: string;
    notes?: string;
    company?: {
        company_name?: string;
        company_type?: TCompanyTypes;
        website_url?: string;
    };
    project_name?: string;
    website_url?: string;
    job_title?: string;
    organization?: {
        name: string;
    };
    custom_contact_fields?: Record<string, any>;
}

export interface ICreateProject {
    event_name: string;
    event_type: string;
    event_timezone: string;
    add_user_id?: string;
}

export interface ICreateProject {
    event_name: string;
    event_type: string;
    event_timezone: string;
    add_user_id?: string;
}

export interface ICreateProjectWithExtendedData {
    event_name: string;
    event_type: string;
    lead_source?: string | null;
    event_date?: string | null;
    event_end_date?: string | null;
    event_timezone?: string | null;
    event_time_start?: string | null;
    event_time_end?: string | null;
    add_user_id?: string | null;
    stage_id?: string;
}

export interface ICreateOrganizationProps {
    name: string;
    website_url?: string;
    phone_number?: string;
    address?: string;
    company_type?: string;

    client:
        | INewClientInOrganizationFields
        | IExistingClientInOrganizationFields;
}

export interface IEditOrganizationProps
    extends Omit<ICreateOrganizationProps, 'client'> {
    _id: string;
}

export type TNylasIntegrationType = 'calendar' | 'email';

export type TGoogleIntegrationType = 'contacts' | 'calendar' | 'gmail';

export type TGoogleRedirectionState =
    | 'calendar'
    | 'gmail'
    | 'contacts'
    | 'connections'
    | 'referrals'
    | 'popup'
    | 'integrations'
    | 'home'
    | 'pipeline'
    | 'workspace'
    | 'contactView'
    | 'setup';

export interface IZoomAuthProviderResponse {
    zoom_provider?: {
        zoom_email: string;
        zoom_account_id: string;
    };
}

export interface ICanvaAuthProviderResponse {
    canva_provider?: {
        display_name: string;
        canva_user_id: string;
    };
}

export interface IUserCreateVideoConferenceResponse {
    uuid: string;
    id: number;
    host_id: string;
    host_email: string;
    status: string;
    start_url: string;
    join_url: string;
    password: string;
}

export interface IGetGoogleAuthURLRequest {
    preferredGoogleAccountEmail: string;
    requestedIntegrationType: TGoogleIntegrationType;
    originState: TGoogleRedirectionState;
    originQueryParams: Record<string, string>;
}

export interface IBaseSetAuthCodeRequest {
    currUserId: string;
    authCode: string;
}

export interface IBaseSetAuthCodeWithValidationCodeRequest
    extends IBaseSetAuthCodeRequest {
    validationCode: string;
}

export interface ISetNylasAuthCodeRequest extends IBaseSetAuthCodeRequest {
    stateString: string;
}

export interface ISetQuickbooksAuthCodeRequest {
    stateString: string;
    realmId: string;
    authCode: string;
}

export interface ISetGoogleAuthCodeRequest
    extends IBaseSetAuthCodeWithValidationCodeRequest {
    integrationType: string;
}

export interface ISetGoogleAuthCodeResponse {
    _id: string;
    curr_google_integrations: {
        account_email: string;
        google_integration_permissions: IGoogleIntegration['google_integration_permissions'];
        company_id: string;
    };
}

export interface IUpdateCalendarProviderRequest {
    enable_sharing: boolean;
    emails: string[];
    calendarsList: IImportCalendarRequest[];
    enableImport: boolean;
    is_meeting_note_taker_enabled?: boolean;
}

export interface IImportCalendarRequest {
    $$hashKey?: string;
    calendar_color: string;
    calendar_id: string;
    calendar_name: string;
    imported: boolean;
    is_primary: boolean;
}

export interface IGetNylasAuthURLRequest {
    return_url: string;
    origin_state: TNylasIntegrationType;
    provider: string;
    email: string;
}

export interface ICalendarProvidersResponse {
    _id: string;
    calendar_provider: ICalendarProvider;
}

export interface ICalendarProvider {
    calendar_importer: {
        _id: string;
        account_email: string;
        calendar_imports: ICalendarImport[];
        enabled: boolean;
        revoked: boolean;
    };
    google_calendar_shares: { email: string; _id: string }[];
    main_email: string;
    shares_enabled: boolean;
    import_enabled: boolean;
    is_meeting_note_taker_enabled?: boolean;
    provider: TEmailProvider;
    _id: string;
    _type: TCalendarProvider;
}

export type TCalendarProvider =
    | 'GoogleCalendarProvider'
    | 'NylasCalendarProvider';

export interface ICalendarImport {
    _id: string;
    calendar_color: string;
    calendar_id: string;
    calendar_name: string;
    created_at: string;
    enabled: boolean;
    updated_at: string;
}

export interface IGetNylasAuthURLRequest {
    return_url: string;
    origin_state: TNylasIntegrationType;
    provider: string;
    email: string;
}

export type TEmailProvider = 'mandrill' | 'gmail' | 'nylas';

export type TEmailProviderName =
    | 'yahoo'
    | 'aol'
    | 'outlook'
    | 'eas'
    | 'ews'
    | 'Gmail'
    | 'icloud';

export interface IEmailProvider {
    email: string;
    name: TEmailProviderName;
    text: string;
    type: TEmailProvider;
    is_custom_domain: boolean;
}

export interface IEmailProvidersResponse {
    providers_list: IEmailProvider[];
    selected_provider: TEmailProvider;
}

export interface IGetNylasAuthURLRequest {
    return_url: string;
    origin_state: TNylasIntegrationType;
    provider: string;
    email: string;
}

export interface ICreateEventWithClientResponse {
    user: ICreateEventWithClientResponseUser;
    workspace: ICreateEventResponse;
}

export interface ICreateEventWithClientResponseUser {
    _id?: string;
    first_name?: string;
    last_name?: string;
    full_name: string;
    email: string;
    job_title?: string;
    phone_number?: string;
    notes?: string;
    company?: {
        _id?: string;
        company_name?: string;
        company_type?: string;
        address?: string;
    };
    profile_image_url?: string;
    imported_from?: 'Google';
    address?: string;
    system_user_type?: 'client' | 'vendor';
    contact?: ISaveContactResponse;
}

export interface ICreateEventResponse {
    _id: string;
    stage_id?: string;
    event: IEvent;
}

export interface IGoogleContactsResponse {
    contacts: { user: ISaveContactRequest; user_id?: string }[];
    total_results: number;
    start_index: number;
    items_per_page: number;
}

export interface ISaveContactResponse {
    _id: string;
    system_tags: any;
    created_at: string;
    is_preferred: boolean;
    user_id: string;
    client_id: string;
    vendor_id: string;
    deletable: boolean;
    details: {
        first_name: string;
        last_name: string;
        email: string;
        address: string;
        phone_number: string;
        company_name: string;
        company_type: string;
        website_url: string;
        full_name: string;
    };
    company?: Record<string, any>;
    contact?: Record<string, any>;
    notes?: string;
    user?: IUserResponse;
    should_show_email?: boolean;
    custom_fields?: ICustomFieldValue[];
    interaction?: ILastInteraction;
}

interface ICommissionResponse {
    _id: string;
    amount: number;
    created_at: string;
    eligible_plans: string[];
    type: string;
    updated_at: string;
}

export interface ICampaignResponse {
    _id: string;
    audience: string;
    auto_approve_commissions: boolean;
    campaign_uid: string;
    commission_amount: number;
    commission_type: string;
    commissions: ICommissionResponse[];
    created_at: string;
    description: string;
    discount_amount_text: string;
    discount_duration: string;
    email_message_text: string;
    is_default: boolean;
    is_educator_campaign: boolean;
    name: string;
    payout_terms: string;
    priority: number;
    title: string;
    updated_at: string;
}

export interface IReferralCodeResponse {
    campaign: ICampaignResponse;
    code: string;
    code_owner: {
        full_name: string;
        email: string;
    };
    coupon: string; // Recurly coupon code
}

export interface IReferrerMarketingResponse {
    discount_amount: string | null;
    discount_duration: string | null;
    user: {
        full_name: string | null;
        profile_image: string | null;
    };
    recurly_coupon: string | null;
}

export type TBatchEmailsBlockedReason =
    | 'unsupported_email_intg'
    | 'premium_feature_only'
    | 'max_daily_passed'
    | 'cannot_send_more_then_xxx_a_day'
    | 'running_task';

export type TBatchEmail = {
    _id: string;
    file_attachments: IEmailAttachment[];
    html_body: string;
    image_attachments: IEmailAttachment[];
    last_sent_time: string;
    not_opened_contacts: string[];
    failed_unsubscribed_contacts: string[];
    is_commercial: boolean;
    opened: number;
    opened_contacts: string[];
    recipient_count: number;
    sent: number;
    status: 'init' | 'sending' | 'done';
    subject: string;
};

export interface IGetAllowBulkWorkspaceEmailsResponse {
    allowed: boolean;
    reason?: TBatchEmailsBlockedReason;
}

export interface IGetBatchEmailsResponse {
    allowed: boolean;
    batch_emails: Array<TBatchEmail>;
    last_page: boolean;
    reason?: TBatchEmailsBlockedReason;
}

// The commented out values have not been seen in the wild for a long time
export enum NotificationType {
    account_setup_completed = 'account_setup_completed',
    automations_waitlist_activated = 'automations_waitlist_activated',
    account_setup_failed = 'account_setup_failed',
    add_your_bank_account = 'add_your_bank_account',
    ask_email_verification = 'ask_email_verification',
    agreement_signed = 'agreement_signed',
    ach_success = 'ach_success',
    author_comment_on_opportunity = 'author_comment_on_opportunity',
    automated_workflow_inquiry_arrived_with_date_tbd = 'automated_workflow_inquiry_arrived_with_date_tbd',
    auto_payment_unaccepted_version = 'auto_payment_unaccepted_version',
    brochure_package_selected = 'brochure_package_selected',
    client_upload_attachment = 'client_upload_attachment',
    connection_posted_opportunity = 'connection_posted_opportunity',
    // countersign_agreement = 'countersign_agreement',
    email_deliverability_issue_found = 'email_deliverability_issue_found',
    email_marked_unread = 'email_marked_unread',
    email_unauthorized = 'email_unauthorized',
    // event_complete = 'event_complete',
    expired_credit_card = 'expired_credit_card',
    // file_comment_added = 'file_comment_added',
    flow_automatically_created_by_selected_service = 'flow_automatically_created_by_selected_service',
    flow_awaiting_countersign = 'flow_awaiting_countersign',
    flow_completed = 'flow_completed',
    flow_countersign_ready = 'flow_countersign_ready',
    flow_contract_complete = 'flow_contract_complete',
    flow_deleted = 'flow_deleted',
    flow_payment_received = 'flow_payment_received',
    flow_payment_reminder_sent = 'flow_payment_reminder_sent',
    // flow_published = 'flow_published',
    // flow_published_without_sharing = 'flow_published_without_sharing',
    flow_service_selected = 'flow_service_selected',
    flow_service_selection_changes = 'flow_service_selection_changes',
    flow_signature_collected = 'flow_signature_collected',
    flow_submitted = 'flow_submitted',
    flow_viewed = 'flow_viewed',
    gcal_revoked = 'gcal_revoked',
    nylas_calendar_revoked = 'nylas_calendar_revoked',
    general_task = 'general_task',
    gmail_unauthorized = 'gmail_unauthorized',
    generic = 'generic',
    community_onboarding = 'community_onboarding',
    clients_csv_upload_success = 'clients_csv_upload_success',
    clients_csv_upload_failed = 'clients_csv_upload_failed',
    contact_form_intro = 'contact_form_intro',
    zoom_integration_revoked = 'zoom_integration_revoked',
    canva_integration_revoked = 'canva_integration_revoked',
    qb_integration_revoked = 'qb_integration_revoked',
    update_bank_info = 'update_bank_info',
    product_onboarding = 'product_onboarding',
    hyper_relevant_opportunity = 'hyper_relevant_opportunity',
    legacy_automation_exist_after_first_sf_sent = 'legacy_automation_exist_after_first_sf_sent',
    meeting_today = 'meeting_today',
    new_calendly_meeting = 'new_calendly_meeting',
    // new_conversation_message = 'new_conversation_message',
    new_feed_message = 'new_feed_message',
    new_hot_inquiry = 'new_hot_inquiry',
    inquiry_reminder = 'inquiry_reminder',
    new_inquiry = 'new_inquiry',
    new_promotion_inquiry = 'new_promotion_inquiry',
    new_trialer = 'new_trialer',
    ai_meeting_note_generated = 'ai_meeting_note_generated',
    ai_meeting_recap_generated = 'ai_meeting_recap_generated',
    ai_project_recap_generated = 'ai_project_recap_generated',
    ai_audio_recording_remainder = 'ai_audio_recording_remainder',
    network_connection_approval = 'network_connection_approval',
    network_connection_request = 'network_connection_request',
    // not_fully_signed_file = 'not_fully_signed_file',
    payment_pending = 'payment_pending',
    payment_received = 'payment_received',
    payment_without_bank = 'payment_without_bank',
    payment_without_bank_followup = 'payment_without_bank_followup',
    payment_missing_bank_info = 'payment_missing_bank_info',
    premium_referral_campaign = 'premium_referral_campaign',
    project_task = 'project_task',
    proposal_viewed = 'proposal_viewed',
    pay_link_submitted = 'pay_link_submitted',
    public_flow_submitted = 'public_flow_submitted',
    public_flow_new_lead = 'public_flow_new_lead',
    pvl_requested = 'pvl_requested',
    publish_project = 'publish_project',
    reader_comment_on_opportunity = 'reader_comment_on_opportunity',
    referral_awareness = 'referral_awareness',
    referral_commission_payout = 'referral_commission_payout',
    referral_joined = 'referral_joined',
    referral_started_trial = 'referral_started_trial',
    // referral_subscribed = 'referral_subscribed',
    session_canceled = 'session_canceled',
    session_rescheduled = 'session_rescheduled',
    session_scheduled = 'session_scheduled',
    session_scheduled_with_non_ws_member = 'session_scheduled_with_non_ws_member',
    shared_flow_template = 'shared_flow_template',
    shared_template_saved = 'shared_template_saved',
    promotion_purchased = 'promotion_purchased',
    questionnaire_submitted = 'questionnaire_submitted',
    year_in_review = 'year_in_review',
    john_branch_templates_imported = 'john_branch_templates_imported',
    freelancers_union_template_imported = 'freelancers_union_template_imported',
    process_map_generation = 'process_map_generation',
    personal_templates_ready = 'personal_templates_ready',
    sign_up_with_adobe = 'sign_up_with_adobe',
    pdf_to_smarf_ready = 'pdf_to_smarf_ready',
    execution_error = 'execution_error',
    execution_pending_approval = 'execution_pending_approval',
    project_created_from_automation = 'project_created_from_automation',
    client_enrichment = 'client_enrichment',
    task_reminder = 'task_reminder',
    task_due = 'task_due',
    task_complete = 'task_complete',
    task_assigned = 'task_assigned',
    lead_suggestions_imported = 'lead_suggestions_imported',
    bulk_client_enrichment = 'bulk_client_enrichment',
    lead_suggestion_captured = 'lead_suggestion_captured',
    tin_match = 'tin_match',
    tin_mismatch = 'tin_mismatch',
    sign_up_with_prismm = 'sign_up_with_prismm',
    connect_gmail_lead_capture = 'connect_gmail_lead_capture',
    instant_payout_eligible = 'instant_payout_eligible',
    outstanding_dispute = 'outstanding_dispute',
    replace_debit_card = 'replace_debit_card',
    timeline_genius_offer = 'timeline_genius_offer',
    honeybook_update_cp_settings = 'honeybook_update_cp_settings',
    referral_program_unlock = 'referral_program_unlock',
    single_unconverted_lead_followup_suggestion = 'single_unconverted_lead_followup_suggestion',
    multiple_unconverted_leads_followup_suggestions = 'multiple_unconverted_leads_followup_suggestions',
    single_unresponsive_lead_followup_suggestion = 'single_unresponsive_lead_followup_suggestion',
    multiple_unresponsive_leads_followup_suggestions = 'multiple_unresponsive_leads_followup_suggestions',
    honeybook_automations_ai_release = 'honeybook_automations_ai_release'
}

export interface ActivityNotification {
    _id?: string;
    type: NotificationType;
    data?: INotificationData;
    last_version_created_at?: string;
    created_at?: string;
    seen?: boolean;
}

export interface INotificationData {
    _id?: string;
    metadata_id?: string;
    auto_suggestion?: boolean;
    event_name?: string;
    generic_type?: string;
    next_state?: ValueOf<IAppStates>;
    flow_type?: string;
    flow_source?: string;
    value?: number;
    message?: string;
    sender_id?: string;
    file_type?: string;
    is_reminder?: boolean;
    instant_payout_setup?: boolean;
    client_first_name?: string;
    sender_full_name?: string;
    client_full_name?: string;
    days?: number;
    meeting_name?: string;
    meeting_note_id?: string;
    user_id?: string;
    full_name?: string;
    comment?: string;
    first_name?: string;
    n_comments?: number;
    looking_for?: string;
    date_time_end?: string;
    workflow_id?: string;
    workflow_title?: string;
    user_action_context?: any;
    flow_name?: string;
    sender_name?: string;
    date_end?: string;
    client_id?: string;
    contact_id?: string;
    recipient_name?: string;
    company_id?: string;
    trialer_id?: string;
    timezone?: string;
    trialer_name?: string;
    session_name?: string;
    payment_id?: string;
    connection_uid?: string;
    price?: number;
    email_id?: string;
    discount_amount?: string;
    discount_duration?: string;
    payer_name?: string;
    payment_index?: number;
    total_payments_count?: number;
    opportunity_type?: string;
    location?: string;
    workspace_file_type?: string;
    workspace_file?: string;
    stc?: string;
    matching_types?: any[];
    n_types?: number;
    gratuity?: number;
    max_commission_amount?: number;
    instant_payout_theoretical_fee?: number;
    hb_transaction_fee_amount?: number;
    hb_transaction_fee_percentage?: number;
    hb_transaction_fee_fixed_in_cents?: number;
    connection_full_name?: string;
    meeting_title?: string;
    feed_message_id?: string;
    all_day?: boolean;
    is_automated?: boolean;
    is_bank_account_connected?: boolean;
    allow_charged_instant_payout?: boolean;
    is_ach?: boolean;
    date_start?: string;
    client_name?: string;
    referred_user_name?: string;
    project_name?: string;
    calendar_item_id?: string;
    flow_title?: string;
    meeting_date?: string;
    session_date?: string;
    client_signatures_left_count?: number;
    create_draft_from_services_selection?: boolean;
    recompleted?: boolean;
    title?: string;
    all_members_ids?: string[];
    missing_member_sign_data?: any[];
    email_sent_on?: string;
    date_time_start?: string;
    payment_amount?: number;
    workspace_file_title?: string;
    payment_count_description?: string;
    workspace_file_id?: string;
    flow_id?: string;
    payment_invoice?: string;
    event_id?: string;
    opportunity_id?: string;
    opportunity_ids?: string[];
    tip_paid?: number;
    seconds_left_for_instant_depositing?: number;
    paid_at?: string;
    client_email?: string;
    html_body?: string;
    workspace_id?: string;
    sender_first_name?: string;
    event_date?: any;
    project_date?: string;
    hot_lead?: IHotLead;
    folder_id?: string;
    template_id?: string;
    assigned_from?: string;
    service_name?: string;
    process_map_id?: string;
    instance_id?: string;
    execution_id?: string;
    automation_name?: string;
    is_opportunity?: boolean;
    contact_name?: string;
    count?: number;
    lead_suggestion_id?: string;
    successful_lead_enrichment_count?: number;
    due_date?: Date;
    url_link?: string;
    parent_class_name?: 'CompanyContact' | 'Event' | 'ClientOrganization';
    parent_object_id?: string;
    task_title?: string;
    task_id?: string;
    assignee_name?: string;
    assigner_name?: string;
    leads_count?: number;
}

export interface IHotLead {
    cvr: boolean;
    revenue: boolean;
    budget: number;
    is_referral: boolean;
}

export type TActivityNotificationGroup =
    | 'referrals'
    | 'activity_messages'
    | 'overdue_tasks'
    | 'task_reminders'
    | 'payments'
    | 'new_inquiries'
    | 'files';

export interface IGetNotificationsSettingsResponse {
    _id: string;
    notification_settings: {
        new_opportunity_push_enabled: boolean;
        email_digest_enabled: boolean;
        subscribed_vendor_types: string[];
        show_offered_services_opportunities: boolean;
        filtered_vendor_types: string[];
        notification_radius: number;
        notification_groups: Partial<
            Record<TActivityNotificationGroup, boolean>
        >;
    };
}

export interface IGetRecentWebNotificationsResponse {
    unseen_notifications_count: number;
    activity_notifications: ActivityNotification[];
    unseen_activity_notifications: ActivityNotification[];
}

export enum OpportunityTypes {
    paid_opportunity = 'paid opportunity',
    collaboration = 'collaboration',
    offer_services = 'services offered',
    other = 'opportunity'
}

export type TPaymentMethodModelType =
    | 'PaymentMethodChargeableBankAccount'
    | 'PaymentMethodPlainBankAccount'
    | 'PaymentMethodStripeBankAccount'
    | 'PaymentMethodStripeCard';

export interface IPaymentMethod {
    _id: string;
    _type: TPaymentMethodModelType;
    name: string;
    account_name: string;
    last4: string;
    bank_name: string;
    type: string;
    routing_number_last4: string;
    account_number_last4: string;
    account_type: string;
    is_active: string;
    account_subtype: string;
    first_name: string;
    last_name: string;
    transit_number: string;
    institution_number: string;
    doing_business_as_name: string;
    merchant_category_code: string;
    url_product_description: string;
    street_address: string;
    city: string;
    state: string;
    zip_code: string;
    phone_number: string;
    tax_id: string;
    card_role: string;
    bank_role: string;
    debit_card_payment_method_id: string;
    payment_method_type: TPaymentMethod;
    use_instant_payout?: boolean;
}

interface IBatchEmailContactDetails {
    _id: string;
    address?: string;
    company_name?: any;
    company_type?: any;
    created_at?: any;
    email: string;
    first_name: string;
    last_name: string;
    notes?: any;
    phone_number?: string;
    updated_at?: any;
    website_url?: any;
}

interface IBatchEmailContactUser {
    _id: string;
    address?: string;
    client_id: string;
    contact_id: string;
    custom_user_fields: any[];
    email: string;
    full_name: string;
    last_name: string;
    notes?: any;
    phone_number?: string;
    profile_image?: any;
    system_user_type: SystemUserType;
    user_id: string;
    vendor_id?: string;
}

export type TGetBatchEmailContactsResponse = Array<{
    _id: string;
    account_id: string;
    client_id: string;
    client_to_company_id_map: Record<string, string>;
    company_id?: string;
    contact_of_company_id: string;
    contact_of_id: string;
    created_at: string;
    deleted_at?: any;
    details: IBatchEmailContactDetails;
    has_company_info?: boolean;
    invited: boolean;
    is_preferred: boolean;
    manually_added: boolean;
    rating: number;
    system_tags: string[];
    updated_at: string;
    user: IBatchEmailContactUser;
    user_id: string;
    user_ids: string[];
    user_tags: any[];
    vendor_id?: string;
    workspaces_ids: string[];
}>;

export interface IWorkspaceNotificationResponse {
    message: string;
    file_id: string;
    file_owner_id: string;
    seen: boolean;
}

export interface IWorkspacePipelineStageResponse {
    stage_id: string;
    moved_on: string;
    name: PipelineStageType;
}

export interface IEmailAttachmentResponse {
    file_name: string;
    file_mime_type: string;
    file_url: string;
}

export interface IEmailTrackingResponse {
    email_id: string;
    to_user_id: string;
    viewed?: Date;
    bounced_on?: Date;
    source: EmailTrackingSource;
    bounce_description?: string;
    bounce_diagnosis?: string;
}

export interface IWorkspacePipelineDataResponse {
    user_id: string;
    tags: string[];
    blocked_tag: string | null;
    blocked_date: Date | null;
    disable_tracking: boolean | null;
    current_stage?: IWorkspacePipelineStageResponse;
    stages_history?: IWorkspacePipelineStageResponse[];
}

export interface IGenericEmailResponse {
    title: string;
    to: string;
    from: string;
    cc?: string;
    bcc?: string;
    subject: string;
    html_body: string;
    sent_on: Date;
    system_generated: boolean;
    preferences_automations_generated: boolean;
    email_type: EmailType;
    clone_to_referral: boolean;
    thread_id: string;
    message_id: string;
    in_reply_to: string;
    references: string;
    last_checked_at: Date;
    job_id: string;
    email_trackings: IEmailTrackingResponse[];
    owner_notified_not_viewed: boolean;
    last_viewed_at: Date;
    feed_item_id: string;
    workflow_id: string;
    step_id: string;
    file_attachments: IEmailAttachmentResponse[];
    image_attachments: IEmailAttachmentResponse[];
}

export interface IWorkspaceTabInfoResponse {
    name: string;
    icon_url?: string | null;
    workspace_type?: WorkspaceTabType;
    has_members_acting_as_client?: boolean;
}

export interface ICompanyPublicResponse {
    _id: string;
    company_name?: string;
}

export interface IUserPublicResponse {
    _id: string;
    _type?: string;
    first_name?: string;
    last_name?: string;
    email: string;
    phone_number?: string;
    profile_image_url?: string;
    user_type?: string;
    user_type_cd?: number;
    system_user_type?: SystemUserType;
    job_title?: string;
    company_role?: string;
    hb_user_type?: HbUserType[];
    created_at?: string;
    company?: ICompanyPublicResponse;
    full_name?: string;
    initials?: string;
    profile_image?: IImage;
    user_type_name?: UserTypeDisplayName;
}

export interface IUserCustomFieldResponse {
    _id: string;
    name: string;
    value: string;
    type: string;
    order: number;
    linked_id?: string;
}

export interface IWorkspaceUserDataResponse {
    _id: string;
    _type: string;
    workspace_role: WorkspaceRole;
    workspace_member_status: WorkspaceMemberStatus;
}

export interface IWorkspaceMemberResponse extends IUserPublicResponse {
    contact_id?: string;
    notes?: string;
    client_id?: string;
    custom_user_fields?: IUserCustomFieldResponse[];
    workspace_user_data: IWorkspaceUserDataResponse;
    profile_image: IImage;
}

export interface IFileBookedObjectsResponse {
    is_booked_version: boolean;
    is_booked_manually: boolean;
}

export interface ITeamMemberResponse {
    _id: string;
    system_user_type: string;
    created_at: string;
    phone_number: string;
    email: string;
    address: string;
    initials: string;
    full_name: string;
    job_title: string;
    is_pro: boolean;
    profile_image?: IImage;
    is_disabled: boolean;
}

export interface ICompanyBriefLighterResponse {
    _id: string;
    account_id: string;
    subscribed: boolean;
    subscribed_for_free: boolean;
    company_type: string;
    other_company_type?: string;
    company_name: string;
    company_type_name: string;
    description: string;
    website_url: string;
    pending_stripe_migration: boolean;
    payment_gateway_future_requirements_action: any; // Adjust the type as needed
    about: string;
    vimeo_url: string;
    blog_url: string;
    street_address: string;
    city: string;
    zip_code: string;
    state: string;
    phone_number: string;
    email: string;
    allow_client_ach: boolean;
    twitter_url: string;
    facebook_url: string;
    instagram_url: string;
    pinterest_url: string;
    show_membership_page: boolean;
    country: string;
    formatted_address: string;
    allow_none_gmail_bulk_email: boolean;
    is_booking_rating_enabled: boolean;
    is_activated: boolean;
    team_members: ITeamMemberResponse[];
    owner: {
        _id: string;
        system_user_type: string;
        created_at: string;
        phone_number: string;
        email: string;
        address: string;
        initials: string;
        full_name: string;
        job_title: string;
        profile_image: {
            url: string;
        };
    };
}

export interface IEmailSignatureResponse {
    _id: string;
    title: string;
    html_body: string;
    version: string;
    created_at: string;
}

export interface IUserSummaryResponse {
    _id: string;
    _type: string;
    first_name: string;
    last_name: string;
    email: string;
    phone_number: string;
    address: string;
    profile_image_url: string;
    twitter_url: string;
    facebook_url: string;
    instagram_url: string;
    pinterest_url: string;
    user_type: UserType;
    user_type_cd: string;
    system_user_type: string;
    job_title: string;
    company_role: string;
    hb_user_type: string;
    disable_client_pvl_requests: boolean | null;
    created_at: string;
    company?: ICompanyBriefLighterResponse;
    full_name: string;
    initials: string;
    is_company_owner: boolean;
    profile_image: IImage;
    email_signatures: IEmailSignatureResponse[];
    user_type_name: UserTypeDisplayName;
}
export interface IWorkspaceCreateProject {
    full_name: string;
    email: string;
    event_name: string;
    event_type: string;
    lead_source: string;
    phone_number: string;
    address: string;
    system_user_type: SystemUserType;
    event_date: Date | null;
    save_to_feed: boolean;
    thread_id: string;
    company?: {
        company_name?: string;
        company_type?: TCompanyTypes;
        website_url?: string;
        company_other?: string | null;
    };
}

export interface IWorkspaceBaseResponse {
    _id: string;
    active_state: boolean;
    company_id: string;
    created_at: string;
    creator: TWorkspaceCreator;
    creator_account_id: string;
    creator_company_brand_color: string;
    creator_company_id: string;
    creator_id: string;
    dismissed_feed_pvl_request?: boolean | null;
    event: IEvent;
    event_name: string;
    has_paid_payments: boolean;
    has_sent_files: boolean;
    has_signed_files: boolean;
    new_lead_email?: string | null;
    onboarding_bot_actions?: unknown;
    onboarding_bot_state?: WorkspaceOnboardingBotState | null;
    referral_type?: string;
    remove_action: unknown;
    sent_link_on?: string;
    user_ids?: string[];
    vendor_id?: string;
    workspace_notifications: IWorkspaceNotificationResponse[];
    workspace_source?: SourceType | null;
    workspace_status_cd: number;
    workspace_status_name: string;
    workspace_status_type: string;
    workspace_tab_info: IWorkspaceTabInfoResponse;
    workspace_type: WorkspaceType | null;
}

export interface IContractSignatures {
    signee_id: string;
    signed_on: unknown;
}

export interface IFileContentResponse {
    file_version: number;
    sent_on: Date | null;
    created_on: Date | null;
    is_booked_version: boolean;
    status: WorkspaceFileStatus;
    status_cd: number;
    is_invalidated: boolean;
    content_invalidation: unknown;
    vendor_proposal: unknown;
    agreement: unknown;
    payments_container: unknown;
    accepted: unknown;
    contract_signatures?: IContractSignatures[];
}

export interface IFileVersion {
    file_version: string;
    is_active: boolean;
    is_booked: boolean;
}

export interface IFileVersionContainerResponse {
    draft_editable: boolean;
    has_booked_version: boolean;
    history_contents: IFileContentResponse[];
    active_content: IFileContentResponse;
    draft_content: IFileContentResponse;
    versions: IFileVersion[];
}

export interface IWorkspaceFileSummaryResponse {
    _id: string;
    status: WorkspaceFileStatus;
    status_cd: number;
    created_at: string;
    file_title: string;
    file_type: WorkspaceFileType;
    file_type_cd: number;
    last_edited_on: string;
    sent_on: string;
    currency: Currency;
    payment_type: PaymentType;
    allow_gratuity: boolean;
    expired?: boolean;
    last_edited_by: string;
    origin_event_date: string;
    status_type: string;
    status_name: string;
    couple_card_id: string;
    owner: IUserSummaryResponse;
    is_canceled: boolean;
    active_file_version: string;
    file_version: string;
    event: {
        _id: string;
        event_name: string;
        event_date: string;
        event_end_date: string;
        event_type: string;
        event_location: string;
        venue_name: string;
        created_at: string;
    };
    workspace_status_type: string;
    has_pending_payment: boolean;
    has_milestone_payment: boolean;
    company: ICompanyBriefLighterResponse;
    request_active: boolean;
    active_state: boolean;
    deletable: boolean;
    workspace_file_image: IImage;
    file_version_container: IFileVersionContainerResponse;
    vendor_proposal: {
        total_price: string;
    };
    payments_container: {
        accepted_payment_method: string;
        payments: TWorkspaceFilePayment[];
    };
    is_new_brochure: boolean;
    source?: string;
    is_booked_version: boolean;
    is_booked_manually: boolean;
}

export interface IPreferredVendorsListResponse {
    _id: string;
    created_from_flow_type: PvlFlowType;
    preferred_vendor_ids: string[];
    notify_referred_vendors: boolean;
    creator_id: string;
    sent_on: string | null;
    vendor_attached_pvl_at: string | null;
    requested_by_user_id: string | null;
    requested_vendor_types: TCompanyTypes[] | null;
    requested_at: string | null;
    request_dismissed_by_user_ids: string[] | null;
    workspace_id: string | null;
    workspace_file_id: string | null;
}

export interface ISavePreferredVendorsListResponse {
    _id: string;
    preferred_vendors_lists: IPreferredVendorsListResponse[];
}

export interface IWorkspaceScheduledSessionResponse {
    _id: string;
    title: string;
    date_time_start: string;
    date_time_end: string;
    date_start: string;
    all_day: boolean;
    flow_id: string;
    owner_id: string;
    flow_title: string | null;
}

export interface IFlowQuestionResponse {
    block_id: string;
    connected_variable_id: string;
    is_required: boolean;
}

export interface IFlowVariableResponse {
    variable_id: string;
    connected_block_id: string;
    value: unknown;
    invalidated_on: string | null;
    diverged_on: string | null;
    type: string; // TODO
    raw_value?: unknown;
    data_source_type?: string;
    data_source_property?: string;
    is_manual_value?: boolean;
}

export interface IAutoExpirationResponse {
    expired: boolean;
    state: AutoExpirationState | null;
    started_on: string | null;
    ends_on: string | null;
    unit_count: number | null;
    unit_type: DurationUnitType | null;
    start_expire_condition: FlowStartExpirationCondition | null;
    show_in_flow: boolean | null;
    is_forced_expired: boolean;
    message: string | null;
}

export interface IWorkspaceFlowConfigurationResponse {
    expired: boolean;
    title: string;
    public_flow_workflow_id: string | null;
    lead_form_contact_tag_ids: string[] | null;
    lead_form_project_tag_ids: string[] | null;
    thumbnail_image: IImage | null;
    configuration: {
        default_email_template_id: string | null;
        is_redirect_url_on: boolean;
        redirect_url: string;
        require_authentication: boolean;
        create_draft_from_services_selection: boolean;
        flow_template_for_draft: string | null;
        cta_button_text: string | null;
        is_logo_enabled: boolean;
        is_workflow_activated: boolean | null;
        is_email_confirmation_activated: boolean;
        auto_expiration: IAutoExpirationResponse;
        assigned_members: IFlowConfigurationMembers;
        flow_link_slug: string;
        flow_link_slug_reached_limit: boolean;
    };
}

export interface IFlowThemeResponse extends IFlowTheme {}

export interface IRelatedFlowResponse {
    related_flow_id: string;
    relation_type: string;
}

export interface IShareFlowPendingTaskDataResponse {
    subject: string;
    html_body: string;
    file_attachments: unknown[]; // TODO
    image_attachments: unknown[]; // TODO
    time_to_send: {
        hour: string;
        day: string;
    };
}

export interface IFlowAttributesResponse extends IFlowConfigurationResponse {
    _id: string;
    title: string;
    original_theme: IFlowThemeResponse | null;
    company_id: string;
    workspace_id: string;
    project_id: string;
    owner_id: string;
    is_template: boolean;
    flow_type: FlowType;
    source_attribution: TemplateSourceType;
    viewed_by_member: boolean;
    starred: boolean | null;
    path: string | null;
    created_at: string;
    folder_id: string | null;
    status: TFlowStatus;
    scheduled_to_be_sent: string | null;
    revision_count: number;
    last_viewed_at: string | null;
    flow_tags: FlowTag[] | null;
    first_published_at: string | null;
    flow_url: string;
    first_viewed_at: string | null;
    currency: Currency;
    deactivated_public_flow: boolean | null;
    latest_revision_id?: string;
    is_published: boolean;
    is_booked: boolean;
    is_deletable: boolean;
    related_flows: IRelatedFlowResponse[];
    was_already_published: boolean;
    published_at: string | null;
    published_by_user: {
        full_name: string;
    } | null;
    card_on_file_enabled: boolean;
    canceled: {
        canceled_on: string;
    } | null;
    share_flow_pending_task_data: IShareFlowPendingTaskDataResponse | null;
    event_name: string;
    is_workspace_archived: boolean;
    expired?: boolean;
}

export interface IFlowPagesResponse {
    page_id: string;
    order: number;
    was_viewed?: boolean;
}

export interface IWorkspaceFlowResponse extends IFlowAttributesResponse {
    pages: IFlowPagesResponse[];
    page_info?: IPageInfoResponse;
    pages_info?: IFlowPagesResponse[];
    questions: IFlowQuestionResponse[];
    variables?: IFlowVariableResponse[];
    has_invoice: boolean;
    has_contract: boolean;
    actions: unknown[]; // TODO: actionable_blocks_status
    thumbnail?: IImage;
    thumbnail_image?: IFlowThumbnail;
    is_flow_thumbnail_visible_to_client: boolean;
    brand_color: string;
}

export interface IWorkspaceResponse extends IWorkspaceBaseResponse {
    disable_client_pvl_requests: boolean | null;
    flows: IWorkspaceFlowResponse[];
    members: IWorkspaceMemberResponse[];
    preferred_vendors_lists: IPreferredVendorsListResponse[] | undefined;
    scheduled_sessions: IWorkspaceScheduledSessionResponse[];
    sessions_with_unrelated_member: string[] | null;
    show_archived_files: boolean;
    workflow_id: string | null;
    workflow_name: string | null;
    workspace_files: IWorkspaceFileSummaryResponse[];
    workspace_pipeline_data: IWorkspacePipelineDataResponse;
}

export type SocialNetwork =
    | 'facebook'
    | 'twitter'
    | 'linkedin'
    | 'instagram'
    | 'apple';

export interface SocialNetworkAccount {
    connected_as: string; // the email address associated with the social network account
    social_network_type: SocialNetwork;
    social_network_type_cd: number;
    user_id: string;
}

export interface IPicTimeCouponResponse {
    eligible: boolean;
    reason?: string;
    source?: string;
}

export type TFlowSource =
    | 'manual'
    | 'public_flow'
    | 'setup_guide'
    | 'smart_file_wizard';

export interface IPicTimeIntegrationInfoResponse {
    auto_create_project: boolean;
    auto_share_to_client: boolean;
}

export type TGetNextPaymentResponse = { attributes: ISubscriptionInvoice }[];

export interface IResetViewOnlyLinkResponse {
    view_only_flow_url: string;
}

export interface IPicTimeAuthUrl {
    auth_url: string;
}

export interface ITheKnotAuthUrl {
    auth_url: string;
}

export interface IGetCalendarEventsOnDateResponse {
    meetings: ICreateCalendarItemResponse[];
    lead_events: ILeadEvent[];
    booked_events: IBookedEvent[];
}

export interface ILeadEvent {
    _id: string;
    event_name: string;
    event_date?: string;
    event_end_date?: string;
    event_time_start?: string;
    event_time_end?: string;
    event_location?: string;
    event_timezone?: TShortTimezone;
    event_budget?: number;
    event_guests?: number;
    event_services?: string;
    venue_name?: string;
    is_booked?: boolean;
}

export interface IBookedEvent extends ILeadEvent {}

export interface ISendLogin2FAResponseSuccess {}

export interface ISendLogin2FAResponseError extends HBError {
    error: boolean;
    error_data: any;
    error_message: string;
    error_type: 'HBTwoFactorAuthError';
    is_timeout: boolean;
}

export type TSendLogin2FAResponse =
    | ISendLogin2FAResponseSuccess
    | ISendLogin2FAResponseError;

export interface IValidate2FACodeResponseSuccess extends IUserResponse {}

export interface IValidate2FACodeResponseError {
    error: boolean;
    error_data: any;
    error_message: string;
    error_type: 'HBTwoFactorAuthError' | 'HBTwoFactorAuthDosError';
    is_timeout: boolean;
}

export type TValidate2FACodeResponse =
    | IValidate2FACodeResponseSuccess
    | IValidate2FACodeResponseError;

export interface IUpdate2FAStateResponseSuccess
    extends Pick<IUserResponse, 'login_2fa_enabled'> {}

export interface IUpdate2FAStateResponseError {
    error: boolean;
    error_data: any;
    error_message: string;
    error_type: 'HBUserError';
    is_timeout: boolean;
}

export type TUpdate2FAStateResponse =
    | IUpdate2FAStateResponseSuccess
    | IUpdate2FAStateResponseError;

export interface IPendingTeamMemberInvite {
    _id: string;
    company_id: string;
    invitee_full_name: string;
    invitee_email: string;
    company_name: string;
    inviter_email: string;
}

export interface HBError {
    error: boolean;
    error_data: any;
    error_message: string;
    error_type: string; // TODO: enum
    is_timeout: boolean;
    note: string;
}

export interface HBUserError {
    data?: {
        error_message: string;
    };
    message: string;
}

export interface IAdditionalCompanyInfo {
    no_of_employees: string | null;
    year_of_establishment: string | null;
}

export interface ITag {
    _id: string;
    color: string;
    tag_name: string;
}

export interface IUpdateTag extends ITag {
    count: number;
}

export type TReferralSuggestion = {
    date: string;
    source: string;
    user: {
        company: {
            address: null | string;
            city: null | string;
            company_name: null | string;
            company_type: null | string;
            phone_number: null | string;
            state: null | string;
            website_url: null | string;
        };
        email: string;
        first_name: string;
        full_name: string;
        id: string;
        last_name: string;
        profile_image: IImage;
    };
};

export type IChangePasswordData = {
    id: string;
    current_password: string;
    new_password: string;
};

export type IStartPhoneVerificationType = {
    userId: string;
    type: TMethod2FA;
};

export interface IChangePasswordResponseSuccess extends IUserResponse {}

export interface IChangePasswordResponseError {
    error: boolean;
    error_data: any;
    error_message: string;
    error_type: 'HBCantReusePasswordError';
    is_timeout: boolean;
}

export type TChangePasswordResponse =
    | IChangePasswordResponseSuccess
    | IChangePasswordResponseError;

export interface ISendPasswordLinkResponseSuccess extends IUserResponse {}

export interface ISendPasswordLinkResponseError {
    error: boolean;
    error_data: any;
    error_message: string;
    error_type: string;
    is_timeout: boolean;
}

export type TSendPasswordLinkResponse =
    | ISendPasswordLinkResponseSuccess
    | ISendPasswordLinkResponseError;

export type IUpdatePhoneNumberForTwoFactorAuthData = {
    phone_number_for_two_factor_auth: string;
    email_code?: string;
    sms_code?: string;
};

export interface IUpdatePhoneNumberForTwoFactorAuthResponseSuccess {}

export interface IUpdatePhoneNumberForTwoFactorAuthResponseError {
    error: boolean;
    error_data: any;
    error_message: string;
    error_type: string;
    is_timeout: boolean;
}

export type TUpdatePhoneNumberForTwoFactorAuthResponse =
    | IUpdatePhoneNumberForTwoFactorAuthResponseSuccess
    | IUpdatePhoneNumberForTwoFactorAuthResponseError;

export type TWorkspaceFileStatuses =
    | 'proposal_created'
    | 'proposal_sent'
    | 'proposal_seen'
    | 'agreement_seen'
    | 'agreement_signed'
    | 'payments_seen'
    | 'retainer_paid'
    | 'payment_done'
    | 'draft'
    | 'in_progress'
    | 'viewed'
    | 'sent'
    | 'new'
    | 'completed';

export type TWorkspaceFilePaymentTypes = 'recurring';

export type TWorkspaceFilePayment = {
    due_date: Date;
    amount: number;
    is_paid: boolean;
    is_pending: boolean;
};

export type TWorkspaceFilePaymentsContainer = {
    payments: TWorkspaceFilePayment[];
};

export type TWorkspaceFileSignee = {
    signee_id: string;
    signed_on: Date;
};

export type TWorkspaceFile = {
    _id: string;
    status: TWorkspaceFileStatuses;
    expired: boolean;
    file_title: string;
    owner: {
        _id: string;
        full_name: string;
        company: {
            _id: string;
        };
    };
    has_pending_payment: boolean;
    payment_type: TWorkspaceFilePaymentTypes;
    file_type: WorkspaceFileType;
    file_type_cd: number;
    company: Company;
    sent_on: Date;
    created_at: Date;
    status_cd: number;
    file_version_container: {
        active_content: {
            payments_container: TWorkspaceFilePaymentsContainer;
            contract_signatures: TWorkspaceFileSignee[];
            status_cd: any;
        };
        versions: { file_version: number; is_active: boolean }[];
    };
    payments_container: TWorkspaceFilePaymentsContainer;
    active_file_version: number;
    deletable: boolean;
    is_canceled: boolean;
    currency: string;
    is_booked_manually: boolean;
    is_booked_version: boolean;
    file_version: number;
};

export type TBlockWorkspacePayload = {
    notify_members: boolean;
    reason: string;
};

export type TBlockWorkspaceResponse = {
    _id: string;
    created_at: string;
    event: {
        _id: string;
    };
    workspace_files: unknown[];
    workspace_pipeline_data: {
        _id: string;
        blocked_date: string;
        blocked_tag: string;
        created_at: string;
        current_stage: {
            _id: string;
            created_at: string | null;
            moved_on: string;
            stage_id: string;
            updated_at: string | null;
        };
        disable_tracking: boolean | null;
        tags: string[];
        updated_at: string;
        user_id: string;
    };
    active_state: boolean;
};

export interface ICompanySpace {
    _id: string;
    name: string;
}

export interface INewCompany {
    _id: string;
    company: Company;
}

export interface FinanceAccountStatus {
    account_closed_ts: null | string;
    application_approved_ts: null | string;
    application_awaiting_documents_ts: null | string;
    application_canceled_reason: null | string;
    application_canceled_ts: null | string;
    application_denied_reason: null | string;
    application_denied_ts: null | string;
    application_eligibility_ts: string | null;
    application_requested_ts: null | string;
    application_requests_count: number;
    awaiting_documents: null | boolean;
    close_reason: null | string;
    close_request_resource: null | string;
    close_user_feedback: null | string;
    closed_by: null | string;
    created_at: string;
    exposed_to_finance_app_ts: null | string;
    process_payments: null | boolean;
    updated_at: string;
    _id: string;
}

export interface IUpdateCalendarColorData {
    calendar_color_type: string;
    color: string;
}

export interface ICalendarColors {
    _id: string;
    created_at: string;
    updated_at: string;
    user_id: string;
    booked_projects_color: string;
    calendly_color: string;
    meetings_color: string;
    payments_color: string;
    spaces_color: Record<string, string>;
    team_members_color: Record<string, string>;
    tentative_projects_color: string | null;
}

export interface ICalendarColorResponse {
    _id: string;
    calendar_colors: ICalendarColors;
}

export type Dummy = any;

export interface QueryStringTrialData {
    extend_trial?: string | string[];
    trial_type?: string | string[];
    utm_campaign?: string | string[];
}

// TODO: Get real response from backend
export interface ISsoLoginUserResponse extends IUserResponse {
    bypass_2fa: boolean;
    //TODO sso_login_user.json_rabl
}

export interface IContractDropdownField {
    category: string;
    data_source_property: string;
    data_source_sub_property?: string;
    data_source_type: string;
    label: string;
    value: string;
}

export interface IPaymentEvent {
    _id: string;
    event_name: string;
    payment_hb_transfer_to_vendor?: number;
    payment_amount: number;
    payment_count_description: string;
    is_milestone: boolean;
    payment_due_date: string;
    couple_card_id: string;
    event_id: string;
    payment_charge_date?: string;
    payment_id: string;
    flow_id?: string;
    payment_date_is_tbd: boolean;
    payment_milestone_title?: string;
    payment_milestone_completed_on?: string;
    payment_late_fee: number;
    eligible_for_instant_payout: boolean;
    allow_instant_payout: boolean;
    use_instant_payout: boolean;
    workspace_file_id?: string;
}

export interface IPaymentsResponse {
    late_payments?: IPaymentEvent[];
    imminent_payments?: IPaymentEvent[];
    upcoming_payments?: IPaymentEvent[];
    due_payments?: IPaymentEvent[];
    paid_payments?: IPaymentEvent[];
}

export type TCalendarAvailabilityType = 'free' | 'busy';

// TODO: Define more precise types instead of any
export interface ICalendarProjectEvent {
    _id: string;
    event_name: string;
    availability_type: TCalendarAvailabilityType;
    event_details: string;
    event_guests?: number;
    event_location: string;
    event_budget?: number;
    event_date: string;
    event_end_date: string;
    event_time_start: string;
    event_time_end: string;
    event_timezone: TShortTimezone;
    is_booked: boolean;
    is_team_member_event: boolean;
    spaces: any[];
    all_event_member_ids: string[];
    event_member_names: any[];
    workspaces_archived: boolean[];
    workspaces_booked: boolean[];
    workspaces_ids: string[];
    created_at: string;
    updated_at: string;
}

export interface ICalendarProjectsResponse {
    future_events: ICalendarProjectEvent[];
    past_events: ICalendarProjectEvent[];
    tbd_events: ICalendarProjectEvent[];
}

export interface IPaymentEvent {
    _id: string;
    event_name: string;
    payment_hb_transfer_to_vendor?: number;
    payment_amount: number;
    payment_count_description: string;
    is_milestone: boolean;
    payment_due_date: string;
    couple_card_id: string;
    event_id: string;
    payment_charge_date?: string;
    payment_id: string;
    flow_id?: string;
    payment_date_is_tbd: boolean;
    payment_milestone_title?: string;
    payment_milestone_completed_on?: string;
    payment_late_fee: number;
    eligible_for_instant_payout: boolean;
    allow_instant_payout: boolean;
    use_instant_payout: boolean;
    workspace_file_id?: string;
}

export interface IPaymentsResponse {
    late_payments?: IPaymentEvent[];
    imminent_payments?: IPaymentEvent[];
    upcoming_payments?: IPaymentEvent[];
    due_payments?: IPaymentEvent[];
    paid_payments?: IPaymentEvent[];
}

export interface IIntegratedCalendar
    extends Omit<IImportCalendarRequest, '$$hashKey'> {}

export interface IGetUserTaxEntitiesResponseSuccess {
    data?: [];
}

export interface IGetUserTaxEntitiesResponseError {
    error: boolean;
    error_data: any;
    error_message: string;
    error_type: string;
    is_timeout: boolean;
}

export type TGetUserTaxEntitiesResponse =
    | IGetUserTaxEntitiesResponseSuccess
    | IGetUserTaxEntitiesResponseError;

export interface InquiryFollowUpResponse {
    is_gate_open: boolean;
    opt_in: boolean;
    timeframe: 'DAY';
    timeframe_value: TInquiryFollowUpTimeFrameValue;
    user_id: string;
}

export interface IDefaultAutoExpirationResponse {
    default_auto_expiration_period: string | null;
}

export interface IResetByGuidRequest {
    userId: string;
    guid: string;
    fileId?: string;
    eventId?: string;
}

export interface IPublishContactFormsLeadSource {
    contact_forms: IContactForm[];
    hb_url?: string | null;
}

export interface IProjectDateResponse {
    project_dates: IProjectDate[];
}

export interface IHappyMoments {
    notes_survey?: boolean;
    nps?: boolean;
    honeyversary?: { years: number };
    transactions?: { count: number };
    projects_booked?: { count: number };
}

export interface IAtwsStatus {
    onboarding_created: boolean;
    onboarding_created_at: string | null;
    onboarding_event_id: string | null;
    trial_created: boolean;
    trial_created_at: string | null;
    trial_event_id: string | null;
}

export interface PaginationProps {
    order_by?: 'asc' | 'desc';
    per_page?: number;
}

export interface IBaseFile {
    filename: string;
    filesize: number;
    filetype: string;
    type: string;
    url: string;
}

export type IFileDetails = IBaseFile;

export interface IFileDetailsCloudinary extends IBaseFile {
    cloudinary_public_id: string;
    cloudinary_sub_account: string;
    cloudinary_url: string;
    height: number;
    width: number;
    phash: string;
}

export type FileDetails = IFileDetails | IFileDetailsCloudinary;

export interface IFileDetailsPayload {
    filesDetails: FileDetails[];
}

export interface IUpdateAssetsResponse {
    cover_images: IImageAsset[];
    company_assets: IFileAsset[];
}

export interface IServerAssetsUploadResult {
    images: IImageAsset[];
    files: IFileAsset[];
}

type TLastInteractionType = 'manual' | 'email' | 'call' | 'meeting';

type TLastInteractionContext =
    | 'Event'
    | 'CompanyContact'
    | 'ClientOrganization';

export interface ILastInteraction {
    type: TLastInteractionType;
    context_type: TLastInteractionContext;
    interaction_date: Date;
}

export interface IAcceptedTOSVersionResponse {
    acceptance_type: 'blocking' | 'notice';
    message_text: string;
}

export interface IAcceptTOSResponse {
    _id: string;
    tos_accepted: boolean;
}

type TLoginSourceType = 'link';

interface ILoginSourceData {
    linkId: string;
}

interface IBaseLoginData {
    email: string;
    password: string;
    trust_device: boolean;
    company_id: string;
    f_token: string;
    'g-recaptcha-response-v3': string | null;
    fingerprint: string;
}

interface ILoginRequestTrackingParams {
    source_template_id?: string;
    template_redeem_code?: string;
    extend_trial?: string;
    trial_type?: string;
    utm_campaign?: string;
}

export interface ILoginRequestData
    extends IBaseLoginData,
        ILoginRequestTrackingParams {
    source?: TLoginSourceType;
    sourceData?: ILoginSourceData;
}

export type TSsoType = 'google' | 'facebook' | 'apple' | 'passkeys';

interface IBaseSsoLoginParams {
    sso_type: TSsoType;
    fingerprint: string;
    auth_token: string;
}

interface IAppleSsoLoginParams extends IBaseSsoLoginParams {
    sso_type: 'apple';
    id_token: string;
    apple_id: string;
}

interface IPasskeysSsoLoginParams extends IBaseSsoLoginParams {
    sso_type: 'passkeys';
    email: string;
}

export type TSsoLoginRequestParams =
    | IBaseSsoLoginParams
    | IAppleSsoLoginParams
    | IPasskeysSsoLoginParams;

export interface SsoLoginUserRequestParams {
    sso_type: TSsoType;
    auth_token?: string;
    // Login with apple params
    id_token?: string;
    name?: string;
    app_id?: string;
}

interface IAppleCredential {
    accessToken: string;
    idToken: string;
}

interface IAppleUserProfile {
    sub: string;
    email?: string;
    email_verified?: boolean;
    name?: {
        firstName?: string;
        lastName?: string;
    };
}

export interface IAppleFirebaseResult {
    credential: IAppleCredential;
    additionalUserInfo: {
        profile: IAppleUserProfile;
        isNewUser: boolean;
        providerId: string;
    };
    user: any;
    operationType: string;
}

export type TCustomFieldUpdate = Omit<ICustomFieldSchema, 'options'> & {
    options?: { _id?: string; label: string; color: string }[];
    entity_type?: string;
};

export interface IPaymentsForWorkspaceResponse {
    _id: string;
    workspace_files: IWorkspaceFileSummaryResponse[];
}

export interface ICreateFilePayload {
    fileType: WorkspaceFileType;
    templateId: string;
    baseFileId: string;
}

export interface ICreateWorkspaceFileResponse {
    _id: string;
    coupled_card_id: string;
    created_at: string;
    file_title: string;
    file_type: string;
    owner: {
        _id: string;
    };
    status_type: string;
    workspace_file_image: IImage;
}

export interface ISendClientPortalLinkData {
    toUser: IOneComposerRecipient;
    subject: string;
    body: string;
    workspaceId: string;
}

export interface ISetStepCompletePayload {
    workspace_file_id: string;
    workspace_id: string;
    event_id: string;
    skipped?: boolean;
}

export interface ISetStepCompleteResponse {
    onboarding_container: Partial<IOnboardingContainer>;
}

export interface IUpdateCompanyWithSuggestedAssetsResponse {
    saved_assets: ISuggestedAssets;
    website_url: string;
}

export interface IIframeBlockResponse extends IBaseBlockResponse {
    type: 'iframe_block';
    url: string;
    height: number;
    width: number;
}

export interface ITimelineContainerResponse {
    _id: string;
    origin_copy_id: string;
    timeline_sections: Array<{
        _id: string;
        section_title: string;
        section_date: string;
        timeline_items: Array<{
            _id: string;
            title: string;
            time: string;
            description: string;
            position: number;
        }>;
        is_empty: boolean;
    }>;
}

export interface ISessionConfigDataFromAngularPipeline {
    type: MeetingType;
    workspace_id: string;
    event_id: string;
    event_name: string;
    title: string;
    description: string;
    location: string;
    duration: {
        _id: string;
        unit_count: number;
        unit_type: DurationUnitType;
    };
    session_config_id: string;
}
